<template>
  <div :class="['flex flex-col bg-cream select-none',
               isEmptyDay ? 'h-screen overflow-hidden' : 'h-full']">

    <!-- ═══ Табы дат: 72px, fixed как NavBar ═══ -->
    <div class="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-50 flex items-end" style="height: calc(72px + var(--safe-top)); background: rgba(235, 225, 210, 0.6); -webkit-backdrop-filter: blur(20px); backdrop-filter: blur(20px);">
      <button
        v-for="(tab, idx) in tabs"
        :key="tab.key"
        :data-guide="tab.key === 'week' ? 'tab-week' : null"
        @click="onTabClick(tab.key)"
        class="flex-1 flex flex-col items-center justify-end transition-colors relative"
        :class="{
          'tab-active': activeTab === tab.key,
          'tab-first': idx === 0,
          'tab-last': idx === tabs.length - 1,
        }"
        :style="[
          activeTab === tab.key
            ? 'background: rgba(250, 243, 232, 0.75); z-index: 2;'
            : 'background: transparent; z-index: 1;',
          'height: 72px; padding-bottom: 12px;',
          idx === 0 ? 'border-radius: 0 14px 0 0;' : idx === tabs.length - 1 ? 'border-radius: 14px 0 0 0;' : 'border-radius: 14px 14px 0 0;'
        ]"
      >
        <!-- Год+ / Месяц / Неделя -->
        <template v-if="!tab.dayDate">
          <div class="flex-1 flex items-end" :class="tab.label ? 'pb-1' : 'pb-2'" v-html="tab.svg" />
          <span v-if="tab.label" style="font-family: Inter, sans-serif; font-weight: 400; font-size: var(--fs-12); line-height: 1; color: var(--ink);">{{ tab.label }}</span>
        </template>

        <!-- Дата (без надписи сегодня/вчера) -->
        <template v-else>
          <span style="font-family: Inter, sans-serif; font-weight: 200; font-size: var(--fs-28); line-height: 1.1; color: var(--ink);">{{ tab.dayDate }}</span>
          <span style="font-family: Inter, sans-serif; font-weight: 400; font-size: var(--fs-12); line-height: 1; color: var(--ink);">{{ tab.monthLabel }}</span>
        </template>
      </button>
    </div>

    <!-- Blur fade зона сверху (под таб-баром) -->
    <div class="blur-fade-top" />

    <!-- Лента недели закреплена отдельно от вертикального скролла. -->
    <WeekDayPicker
      v-if="isWeekView"
      ref="weekStripRef"
      :day-states="stripDayStates"
      :days-with-entries="stripDays"
      :selected-date="selectedPeriodDate"
      :day-kcals="stripKcals"
      :kcal-norm="NORMS.kcal.upper"
      :show-kcal-colors="showKcalColors"
      :planned-days="periodData?.planned_days || []"
      :first-day="diaryFirstDay"
      @select="selectPeriodDay"
      @reach="onStripReach"
      class="fixed left-1/2 -translate-x-1/2 w-full max-w-[430px] z-[46]"
      style="top: calc(72px + var(--safe-top));"
    />

    <!-- Контент (pt-[72px] под fixed таб-бар, скролл) -->
    <div class="flex-1 scrollbar-hide pt-[72px]" :class="isEmptyDay ? 'overflow-hidden' : 'overflow-y-auto pb-24'" ref="listRef">

      <!-- Периоды: неделя / месяц -->
      <template v-if="isPeriodView">

        <!-- Место под закреплённую ленту недели. -->
        <div v-if="isWeekView" aria-hidden="true" :style="{ height: weekStripH + 'px' }" />
        <!-- Недельный отчет за прошлую неделю или лендинг до первого отчета,
             с настройками модулей (components/report/WeekReportCard.vue) -->
        <!-- Период: быстрые диапазоны и шторка дат. Календарь ниже красится
             по своему месяцу (calendarData), а не по выбранному периоду, и
             обводит дни периода; нажатие на день открывает его, как раньше -->
        <!-- Шапка вкладки (вариант 1, решение владельца 27.10.2026): слева
             «Аналитика», справа селектор периода — пилюля с короткими датами,
             нажатие открывает меню (последняя серия, 2 недели, 30 дней, весь
             дневник, свой период). Свой период — два нажатия на календаре,
             второе применяет сразу; пока выбираем, вместо селектора «Отменить».
             Один стиль заголовка, один текста, одна подпись — без полей и
             кнопок, чтобы строка помещалась на любом телефоне. -->
        <div v-if="isMonthView" class="px-3 mt-6 relative">
          <div class="flex items-center justify-between gap-3">
            <h1 class="text-ink whitespace-nowrap" style="font-family: Inter, sans-serif; font-size: var(--fs-26); font-weight: 600; line-height: 1.1; margin: 0;">Аналитика</h1>
            <button v-if="pickMode === 'none'" class="psel" data-guide="an-range" :aria-expanded="menuOpen" @click="menuOpen = !menuOpen">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="shrink-0"><rect x="3" y="5" width="18" height="16" rx="3"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="16" y1="3" x2="16" y2="7"/></svg>
              <span class="truncate">{{ rangeFrom && rangeTo ? shortRange(rangeFrom, rangeTo) : 'период' }}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="shrink-0 transition-transform" :style="{ transform: menuOpen ? 'rotate(180deg)' : '' }"><path d="M6 9l6 6 6-6"/></svg>
            </button>
            <button v-else class="psel psel-cancel" @click="cancelPick">Отменить</button>
          </div>
          <!-- Меню периода под селектором; тап мимо закрывает -->
          <div v-if="menuOpen" class="fixed inset-0 z-[29]" @click="menuOpen = false" />
          <div v-if="menuOpen" class="pmenu">
            <button v-for="it in periodMenu" :key="it.key" class="pitem" :class="{ on: it.on }" @click="pickMenu(it)">
              <span class="flex flex-col items-start min-w-0">
                <span>{{ it.label }}</span>
                <span v-if="it.caption" class="pcap">{{ it.caption }}</span>
              </span>
              <svg v-if="it.on" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else-if="it.key === 'custom'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" class="shrink-0 text-ink/60"><path d="M9 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
        <!-- Сегодняшний день не красится: он не закончен (решение владельца 26.10.2026) -->
        <MonthCalendar
          v-if="isMonthView"
          :day-states="calendarData?.day_states || {}"
          :days-with-entries="calendarDaysNoToday"
          :selected-date="selectedPeriodDate"
          :day-kcals="calendarKcalsNoToday"
          :kcal-norm="NORMS.kcal.upper"
          :show-kcal-colors="showKcalColors"
          :planned-days="calendarData?.planned_days || []"
          :range-from="pickMode === 'none' ? rangeFrom : null"
          :range-to="pickMode === 'none' ? rangeTo : null"
          :pick-mode="pickMode"
          :pick-start="pickStart"
          :max-day="toDateStr(today)"
          :focus-month="calendarFocus"
          @select="selectPeriodDay"
          @pick="onPick"
          @month-change="onMonthChange"
        />

        <!-- Лоадер периода -->
        <div v-if="periodLoading" class="flex justify-center pt-8">
          <div class="w-6 h-6 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
        </div>

        <template v-else-if="periodData">

          <template v-if="isWeekView">
          <!-- Средние КБЖУ за период -->
          <div class="px-3 pt-1">
            <div class="week-completeness-slot" aria-hidden="true" />
            <div class="day-heading">
              <h2>{{ monthSummaryTitle }}</h2>
            </div>
          </div>

            <div class="px-3">
              <div v-if="showKcal" @click="kcalPeriodOpen = !kcalPeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="kcalPeriodOpen = !kcalPeriodOpen">
                <MacroBar label="Ккал" :value="periodData.averages.kcal" unit="ккал" :upper="NORMS.kcal.upper" :lower="NORMS.kcal.lower" :lowerLabel="NORMS.kcal.lowerLabel" color="#EE998C" :flush="kcalPeriodOpen" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showKcal && kcalPeriodOpen" :breakdown="periodData.kcal_breakdown" :days="fiberPeriodDays" label="калории" empty-text="За этот период калорий в дневнике нет." bg="rgba(238, 153, 140, 0.2)" integer @close="kcalPeriodOpen = false" />
              </ExpandTransition>
              <div v-if="showProtein" @click="proteinPeriodOpen = !proteinPeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="proteinPeriodOpen = !proteinPeriodOpen">
                <MacroBar label="Белки" :value="periodData.averages.protein" unit="г" :upper="NORMS.protein.upper" :lower="NORMS.protein.lower" color="#9FC2C1" :flush="proteinPeriodOpen" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showProtein && proteinPeriodOpen" :breakdown="periodData.protein_breakdown" :days="fiberPeriodDays" label="белок" empty-text="За этот период белка в дневнике нет." bg="rgba(159, 194, 193, 0.2)" @close="proteinPeriodOpen = false" />
              </ExpandTransition>
              <div v-if="showFat" @click="cycleFatsPeriod" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleFatsPeriod">
                <MacroBar label="Жиры" :value="periodData.averages.fat" unit="г" :upper="NORMS.fat.upper" :lower="NORMS.fat.lower" color="#EBC06D" :flush="fatsPeriodOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showFat && fatsPeriodOpen === 2" :breakdown="periodData.fat_breakdown" :days="fiberPeriodDays" label="жиры" empty-text="За этот период жиров в дневнике нет." bg="rgba(235, 192, 109, 0.2)" @close="fatsPeriodOpen = 1" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showFat && fatsPeriodOpen >= 1">
                  <div @click="satPeriodOpen = !satPeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="satPeriodOpen = !satPeriodOpen">
                    <MacroBar label="Насыщенные жиры" :value="periodData.fats_breakdown?.sat?.avg_day || 0" unit="г" :upper="satLimit" :norm-text="`до ${satLimit} г`" color="#d7aa49" precise :flush="satPeriodOpen" />
                  </div>
                  <ExpandTransition>
                    <MacroPeriodBlock v-if="satPeriodOpen" :breakdown="periodData.fats_breakdown?.sat" :days="fiberPeriodDays" label="насыщенные жиры" empty-text="Заметных источников насыщенных жиров за период нет." bg="rgba(215, 170, 73, 0.2)" @close="satPeriodOpen = false" />
                  </ExpandTransition>
                  <div @click="o3PeriodOpen = !o3PeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="o3PeriodOpen = !o3PeriodOpen">
                    <MacroBar label="Омега-3" :value="periodData.fats_breakdown?.omega3?.avg_day || 0" unit="г" :upper="O3_NORM.upper" :lower="O3_NORM.lower" color="#eecf8a" precise :flush="o3PeriodOpen" />
                  </div>
                  <ExpandTransition>
                    <MacroPeriodBlock v-if="o3PeriodOpen" :breakdown="periodData.fats_breakdown?.omega3" :days="fiberPeriodDays" label="омега-3" empty-text="Заметных источников омега-3 за период нет." bg="rgba(238, 207, 138, 0.2)" :decimals="2" @close="o3PeriodOpen = false" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <!-- Углеводы: первый тап открывает полоску «Добавленный сахар»,
                   второй — раскладку общих углеводов, третий сворачивает всё (как жиры) -->
              <div v-if="showCarbs" @click="cycleCarbsPeriod" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleCarbsPeriod">
                <MacroBar label="Углеводы" :value="periodData.averages.carbs" unit="г" :upper="NORMS.carbs.upper" :lower="NORMS.carbs.lower" color="#EEAD77" :flush="carbsPeriodOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showCarbs && carbsPeriodOpen === 2" :breakdown="periodData.carbs_breakdown" :days="fiberPeriodDays" label="углеводы" empty-text="За этот период углеводов в дневнике нет." bg="rgba(238, 173, 119, 0.2)" @close="carbsPeriodOpen = 1" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showCarbs && carbsPeriodOpen >= 1">
                  <div @click="freePeriodOpen = !freePeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="freePeriodOpen = !freePeriodOpen">
                    <MacroBar label="Добавленный сахар" :value="periodData.sugars_breakdown?.free?.avg_day || 0" unit="г" :upper="sugarLimit" :norm-text="`до ${sugarLimit} г`" color="#DE8F6A" precise :flush="freePeriodOpen" />
                  </div>
                  <ExpandTransition>
                    <MacroPeriodBlock v-if="freePeriodOpen" :breakdown="periodData.sugars_breakdown?.free" :days="fiberPeriodDays" label="добавленный сахар" empty-text="Заметных источников добавленного сахара за период нет." bg="rgba(222, 143, 106, 0.2)" @close="freePeriodOpen = false" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <div v-if="showFiber" @click="fiberPeriodOpen = !fiberPeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="fiberPeriodOpen = !fiberPeriodOpen">
                <MacroBar label="Клетчатка" :value="periodData.averages.fiber" unit="г" :upper="NORMS.fiber.upper" :lower="NORMS.fiber.lower" color="#A6B779" :flush="fiberPeriodOpen" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showFiber && fiberPeriodOpen" :breakdown="periodData.fiber_breakdown" :days="fiberPeriodDays" @close="fiberPeriodOpen = false" />
              </ExpandTransition>
              <div v-if="showWater" @click="waterPeriodOpen = !waterPeriodOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="waterPeriodOpen = !waterPeriodOpen">
                <MacroBar label="Вода" :value="periodData.averages.water" unit="г" :upper="3000" color="#ABDEEB" :no-norm="true" normText="из еды и напитков" :approx="periodData.water_breakdown?.items?.some(i => i.has_estimate)" :flush="waterPeriodOpen" />
              </div>
              <ExpandTransition>
                <MacroPeriodBlock v-if="showWater && waterPeriodOpen" :breakdown="periodData.water_breakdown" :days="fiberPeriodDays" label="вода" empty-text="За этот период воды в дневнике нет." bg="rgba(171, 222, 235, 0.2)" integer @close="waterPeriodOpen = false" />
              </ExpandTransition>
            </div>
            <div v-if="showVitamins" class="px-3">
              <VitaminBlock :totals="periodData.averages.vitamins" :sex="userSex" :age="userAge" :show-coverage="true" />
            </div>
          <WeekReportCard :key="statusRevision" class="px-3" />
          </template>

          <!-- Период: карточки с графиками по дням (PeriodMetricCard + DayBars).
               Ряды — days из GET /diary/summary?period=range; каждый столбик — день. Скрытое в настройках
               дневника скрыто и здесь. -->
          <template v-else>
            <!-- Пример для новичка: после календаря и его легенды, перед графиками. -->
            <div v-if="isDemo" class="px-3 mt-4">
              <div class="norm-hint">
                <p class="norm-hint__text">Пример: так может выглядеть аналитика после двух месяцев учета</p>
              </div>
            </div>
            <div v-if="!periodData.sufficient" class="px-3 pt-4 pb-6">
              <p class="text-15 text-ink/70 leading-relaxed">Для графиков нужно хотя бы два дня с записями. {{ isVy ? 'Выберите' : 'Выбери' }} другой период или {{ isVy ? 'ведите' : 'веди' }} дневник ещё день.</p>
            </div>
            <div v-else class="px-3 mt-4">
              <PeriodMetricCard v-if="showWeight && weightPoints.length" label="Вес" :sub="weightSub" :value="weightValue" unit="кг" unit-line="кг" :decimals="1" :norm-text="weightNormText">
                <WeightTrendChart :points="weightPoints" :from="periodData.period.from" :to="periodData.period.to" :deficit-kcal="weightDeficitKcal" :deficit-pct="Math.abs(effectiveDeficitPct)" />
              </PeriodMetricCard>

              <PeriodMetricCard v-if="showKcal" label="Энергия" :value="periodData.averages.kcal" unit="ккал" :norm-lines="kcalNormLines" v-model:open="cardOpen.kcal">
                <DayBars data-guide="an-chart" :buckets="kcalBuckets" :color="COLORS.kcal" unit="ккал" :upper="NORMS.kcal.upper" :extra-lines="kcalExtraLines" aria-label="Энергия по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.kcal_breakdown" :days="fiberPeriodDays" label="калории" empty-text="За этот период калорий в дневнике нет." bg="rgba(238, 153, 140, 0.2)" integer @close="cardOpen.kcal = false" />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard norm-band v-if="showProtein" label="Белки" :value="periodData.averages.protein" unit="г" :norm-text="`норма ${NORMS.protein.lower}–${NORMS.protein.upper} г`" v-model:open="cardOpen.protein">
                <DayBars data-guide="an-chart" :buckets="proteinBuckets" :color="COLORS.protein" unit="г" :lower="NORMS.protein.lower" :upper="NORMS.protein.upper" aria-label="Белки по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.protein_breakdown" :days="fiberPeriodDays" label="белки" empty-text="За этот период источников белка в дневнике нет." />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard norm-band v-if="showFat" label="Жиры" :value="periodData.averages.fat" unit="г" :norm-text="`норма ${NORMS.fat.lower}–${NORMS.fat.upper} г`" v-model:open="cardOpen.fat">
                <DayBars data-guide="an-chart" :buckets="fatBuckets" :color="COLORS.fat" unit="г" :lower="NORMS.fat.lower" :upper="NORMS.fat.upper" aria-label="Жиры по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.fat_breakdown" :days="fiberPeriodDays" label="жиры" empty-text="За этот период источников жиров в дневнике нет." />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard v-if="showFat && periodData.fats_breakdown" label="Насыщенные жиры" :value="periodData.fats_breakdown.sat?.avg_day || 0" unit="г" :decimals="1" :norm-text="`ограничение до ${satLimit} г`" :approx="!!periodData.fats_breakdown.sat?.approx" norm-color="var(--chart-limit)" v-model:open="cardOpen.sat">
                <DayBars :buckets="satBuckets" limit-color="var(--chart-limit)" :color="COLORS.sat" unit="г" :decimals="1" :upper="satLimit" aria-label="Насыщенные жиры по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.fats_breakdown.sat" :days="fiberPeriodDays" label="насыщенные жиры" empty-text="Заметных источников насыщенных жиров за период нет." bg="rgba(215, 170, 73, 0.2)" @close="cardOpen.sat = false" />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard norm-band v-if="showCarbs" label="Углеводы" :value="periodData.averages.carbs" unit="г" :norm-text="`норма ${NORMS.carbs.lower}–${NORMS.carbs.upper} г`" v-model:open="cardOpen.carbs">
                <DayBars data-guide="an-chart" :buckets="carbsBuckets" :color="COLORS.carbs" unit="г" :lower="NORMS.carbs.lower" :upper="NORMS.carbs.upper" aria-label="Углеводы по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.carbs_breakdown" :days="fiberPeriodDays" label="углеводы" empty-text="За этот период источников углеводов в дневнике нет." />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard v-if="showCarbs && periodData.sugars_breakdown" label="Добавленный сахар" :value="periodData.sugars_breakdown.free?.avg_day || 0" unit="г" :decimals="1" :norm-text="`ограничение до ${sugarLimit} г`" :approx="!!periodData.sugars_breakdown.free?.approx" norm-color="var(--chart-limit)" v-model:open="cardOpen.sugar">
                <DayBars :buckets="sugarBuckets" limit-color="var(--chart-limit)" :color="COLORS.sugar" unit="г" :decimals="1" :upper="sugarLimit" aria-label="Добавленный сахар по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.sugars_breakdown.free" :days="fiberPeriodDays" label="добавленный сахар" empty-text="Заметных источников добавленного сахара за период нет." bg="rgba(222, 143, 106, 0.2)" @close="cardOpen.sugar = false" />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard norm-band v-if="showFiber" label="Клетчатка" :value="periodData.averages.fiber" unit="г" :norm-text="`норма ${NORMS.fiber.lower}–${NORMS.fiber.upper} г`" v-model:open="cardOpen.fiber">
                <DayBars :buckets="fiberBuckets" :color="COLORS.fiber" unit="г" :lower="NORMS.fiber.lower" :upper="NORMS.fiber.upper" aria-label="Клетчатка по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.fiber_breakdown" :days="fiberPeriodDays" @close="cardOpen.fiber = false" />
                </template>
              </PeriodMetricCard>

              <PeriodMetricCard v-if="showWater" label="Вода" :value="periodData.averages.water" unit="г" norm-text="Единой нормы не существует. Она зависит от нагрузки, влажности воздуха, индивидуальных особенностей" v-model:open="cardOpen.water">
                <DayBars :buckets="waterBuckets" :color="COLORS.water" unit="г" aria-label="Вода по дням" @select="guide.done('an_bar')" @open-day="onBarOpenDay" @zoom="applyRange" />
                <template v-if="!isDemo" #more>
                  <MacroPeriodBlock bare :breakdown="periodData.water_breakdown" :days="fiberPeriodDays" label="вода" empty-text="За этот период воды в дневнике нет." bg="rgba(171, 222, 235, 0.2)" integer @close="cardOpen.water = false" />
                </template>
              </PeriodMetricCard>

              <!-- Список продуктов без данных о витаминах — только до недели: на долгом сроке он бесконечный -->
              <VitaminBlock always-open v-if="showVitamins && !isDemo" :totals="periodData.averages.vitamins" :sex="userSex" :age="userAge" :show-coverage="periodDaysCount <= 7" />
            </div>
          </template>

          <!-- Аналитика от Зои за период (в примере её нет) -->
          <div v-if="!isDemo && !isWeekView" class="mt-2 mb-4 bg-cream rounded-2xl px-3 py-3">
            <p style="font-family: Inter, sans-serif; font-size: var(--fs-20); font-weight: 600; color: var(--ink);" class="mb-3">
              Аналитика за {{ isWeekView ? 'неделю' : 'период' }} от Зои
            </p>

            <!-- Кнопка "Переделать анализ" (над существующим фидбеком) -->
            <button
              v-if="periodZoyaFeedback && !periodZoyaLoading && periodData.sufficient && !periodRedoMode"
              @click="periodRedoMode = true; periodZoyaError = ''"
              class="mb-3 w-full py-3 rounded-2xl bg-ink/10 text-ink text-15 font-semibold active:bg-ink/20 transition-colors"
            >
              Переделать анализ
            </button>

            <!-- Текст фидбека -->
            <div v-if="periodZoyaFeedback && !periodRedoMode" class="zoya-feedback" v-html="formatZoyaFeedback(periodZoyaFeedback)"></div>

            <!-- Загрузка -->
            <div v-if="periodZoyaLoading" class="py-4">
              <div class="flex items-center gap-2">
                <svg class="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#E5E5E5" stroke-width="3"/>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#41291A" stroke-width="3" stroke-linecap="round"/>
                </svg>
                <span class="text-14" style="color: var(--ink); opacity: 0.7;">Зоя анализирует рацион...</span>
              </div>
              <p v-if="isWeekView" class="text-12 mt-1.5 ml-[28px]" style="color: var(--ink); opacity: 0.7;">Подробный анализ за неделю может занять до 1-2 минут</p>
            </div>

            <!-- Ошибка -->
            <p v-if="periodZoyaError" class="text-13 text-error">{{ periodZoyaError }}</p>

            <!-- Секции анализа (только неделя) — всегда видны, даже при недостатке данных -->
            <div v-if="isWeekView && (!periodZoyaFeedback || periodRedoMode) && !periodZoyaLoading"
                 class="mt-2 mb-2 space-y-1.5">
              <p class="text-13 text-ink/70 mb-1">Что включить в анализ:</p>
              <label v-for="sec in WEEK_SECTIONS" :key="sec.id"
                     class="flex items-center gap-2.5 py-1 cursor-pointer">
                <input type="checkbox" :value="sec.id" v-model="selectedSections"
                       class="w-[18px] h-[18px] rounded accent-[#41291A] shrink-0" />
                <span class="text-14 text-ink">{{ sec.label }}</span>
              </label>
            </div>

            <!-- Недостаточно данных -->
            <p v-if="isWeekView && !periodData.sufficient && !periodZoyaLoading" class="text-13 text-ink/70 leading-relaxed mt-2">
              Необходимо набрать хотя бы 5 полных дней учёта для получения аналитики за неделю
            </p>
            <p v-if="!isWeekView && !periodData.sufficient && !periodZoyaLoading" class="text-14 text-ink/70 leading-relaxed">
              Пока данных для аналитики маловато! {{ isVy ? 'Ведите' : 'Веди' }} дневник еще несколько дней и
              {{ isVy ? 'возвращайтесь' : 'возвращайся' }}, разберем {{ isVy ? 'ваше' : 'твое' }} питание!
            </p>

            <!-- Неактивная кнопка при недостатке данных (неделя) -->
            <button
              v-if="isWeekView && !periodData.sufficient && !periodZoyaLoading"
              disabled
              class="mt-3 w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold disabled:opacity-40"
            >
              Получить аналитику
            </button>

            <!-- Кнопки: запрос + отмена -->
            <div v-if="periodData.sufficient && (!periodZoyaFeedback || periodRedoMode) && !periodZoyaLoading"
                 class="mt-3 flex flex-col gap-3">
              <button
                @click="redoPeriodZoyaFeedback"
                :disabled="(isWeekView && selectedSections.length === 0) || (periodRedoMode && !periodZoyaStale)"
                class="w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold disabled:opacity-40 active:scale-[0.98] transition-all"
              >
                {{ periodRedoMode ? 'Переделать анализ' : (isWeekView ? 'Получить аналитику' : 'Запросить аналитику за период') }}
              </button>
              <p v-if="!isWeekView && periodData.period" class="-mt-1 text-13 text-center text-ink/70" style="font-family: Inter, sans-serif;">
                {{ rangeLabel(periodData.period.from, periodData.period.to) }} · {{ periodData.days_tracked }} {{ dayWordOf(periodData.days_tracked) }} учёта
              </p>
              <p v-if="periodRedoMode && !periodZoyaStale"
                 class="-mt-1 text-12 text-center text-ink/70 leading-snug">
                Дневник не менялся с прошлого анализа — результат будет тем же
              </p>
              <button
                v-if="periodRedoMode"
                @click="periodRedoMode = false; periodZoyaError = ''"
                class="w-full py-3 rounded-2xl bg-ink/10 text-ink text-15 font-semibold active:bg-ink/20 transition-colors"
              >
                Отмена
              </button>
            </div>
          </div>

        </template>
      </template>

      <!-- День -->
      <template v-else>

        <!-- Лоадер -->
        <div v-if="loading" class="flex justify-center pt-16">
          <div class="w-6 h-6 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
        </div>

        <template v-else>

          <!-- «Первые шаги» — карточка новичка (services/guide.js), только сегодня -->
          <FirstStepsCard v-if="activeTab === 'today' && guideChecklistVisible" @go="onGuideGo" />

          <!-- Нормы не рассчитаны — говорим об этом прямо.
               Шкалы в этом случае показывают запасные 2000 ккал и средние
               границы БЖУ (см. NORMS ниже): выглядят они точно так же, как
               персональные, и молча выдавать чужие цифры за свои нельзя. -->
          <!-- Пока видна карточка «Первые шаги», блок не показывается: там тот же
               пункт «Рассказать о себе», а после первой записи то же скажет пузырь
               Зои у шкалы калорий (подсказка norms). -->
          <div v-if="!effectiveKcalNorm && !guideChecklistVisible" class="px-3 mt-5">
            <div class="norm-hint">
              <p class="norm-hint__text">
                Нормы пока средние — {{ isVy ? 'ваши' : 'твои' }}
                посчитаю по росту, весу и возрасту.
              </p>
              <RouterLink to="/profile/body" class="norm-hint__btn">Заполнить</RouterLink>
            </div>
          </div>

          <div v-if="entries.length > 0" class="day-heading px-3 mt-6">
            <h2>{{ activeTab === 'today' ? 'Всего за сегодня' : 'Всего за вчера' }}</h2>
          </div>
          <!-- ═══ БЛОК 1: МАКРОНУТРИЕНТЫ ═══ -->
          <template v-if="(anyMacroVisible || showVitamins) && mealGroups.length > 0">

            <!-- Пока связи нет, день показан из кэша (stale-while-revalidate в
                 loadEntries); без пометки человек принимал бы старый снимок за
                 «не сохранилось». Снимается сама: по возврату связи день
                 перечитывается (onReconnect → reloadIfStale). -->
            <p v-if="offline" class="px-3 -mt-1 mb-3 text-13 text-ink/60" style="font-family: Inter, sans-serif;">Показано по последнему снимку, обновится при появлении связи</p>
            <div class="px-3">
              <div v-if="showKcal" data-guide="kcal" @click="kcalDetailsOpen = !kcalDetailsOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="kcalDetailsOpen = !kcalDetailsOpen">
                <MacroBar label="Ккал" :value="totals.kcal" unit="ккал" :upper="NORMS.kcal.upper" :lower="NORMS.kcal.lower" :lowerLabel="NORMS.kcal.lowerLabel" color="#EE998C" :flush="kcalDetailsOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showKcal && kcalDetailsOpen" :entries="entries" macro="kcal" :min-shown="5" bg="rgba(238, 153, 140, 0.2)" empty-text="Сегодня калорий в дневнике пока нет" integer />
              </ExpandTransition>
              <div v-if="showProtein" @click="proteinDetailsOpen = !proteinDetailsOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="proteinDetailsOpen = !proteinDetailsOpen">
                <MacroBar label="Белки" :value="totals.protein" unit="г" :upper="NORMS.protein.upper" :lower="NORMS.protein.lower" color="#9FC2C1" :flush="proteinDetailsOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showProtein && proteinDetailsOpen" :entries="entries" macro="protein" :min-shown="0.5" bg="rgba(159, 194, 193, 0.2)" empty-text="Сегодня белка в дневнике пока нет" />
              </ExpandTransition>
              <!-- Жиры: первый тап открывает полоски «Насыщенные» и «Омега-3»,
                   второй — раскладку общего жира, третий сворачивает всё -->
              <div v-if="showFat" @click="cycleFatsDay" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleFatsDay">
                <MacroBar label="Жиры" :value="totals.fat" unit="г" :upper="NORMS.fat.upper" :lower="NORMS.fat.lower" color="#EBC06D" :flush="fatsOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showFat && fatsOpen === 2" :entries="entries" macro="fat" :min-shown="0.5" bg="rgba(235, 192, 109, 0.2)" empty-text="Сегодня жиров в дневнике пока нет" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showFat && fatsOpen >= 1">
                  <div @click="satDetailOpen = !satDetailOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="satDetailOpen = !satDetailOpen">
                    <MacroBar label="Насыщенные жиры" :value="dayFats.totals.sat" unit="г" :upper="satLimit" :norm-text="`до ${satLimit} г`" color="#d7aa49" precise :loading="fatsLoading || fatsPending" :approx="dayFats.totals.sat_approx" :flush="satDetailOpen" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="satDetailOpen" metric="sat" :entries="entries" :values="dayFats.entries" :parts="dayFats.parts" :loading="fatsLoading" :coverage="dayFats.totals.sat_coverage_pct ?? 100" :estimating="dayFats.totals.estimating || 0" :min="0.3" :decimals="1" bg="rgba(215, 170, 73, 0.2)" empty-text="Заметных источников насыщенных жиров сегодня нет" />
                  </ExpandTransition>
                  <div @click="o3DetailOpen = !o3DetailOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="o3DetailOpen = !o3DetailOpen">
                    <MacroBar label="Омега-3" :value="dayFats.totals.omega3" unit="г" :upper="O3_NORM.upper" :lower="O3_NORM.lower" color="#eecf8a" precise :loading="fatsLoading || fatsPending" :approx="dayFats.totals.omega3_approx" :flush="o3DetailOpen" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="o3DetailOpen" metric="omega3" :entries="entries" :values="dayFats.entries" :parts="dayFats.parts" :loading="fatsLoading" :coverage="dayFats.totals.omega3_coverage_pct ?? 100" :estimating="dayFats.totals.estimating || 0" :min="0.05" :decimals="2" bg="rgba(238, 207, 138, 0.2)" empty-text="Заметных источников омега-3 сегодня нет" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <!-- Углеводы: первый тап — полоска «Добавленный сахар» с личным
                   лимитом, второй — раскладка общих углеводов, третий сворачивает -->
              <div v-if="showCarbs" @click="cycleCarbsDay" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleCarbsDay">
                <MacroBar label="Углеводы" :value="totals.carbs" unit="г" :upper="NORMS.carbs.upper" :lower="NORMS.carbs.lower" color="#EEAD77" :flush="carbsOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showCarbs && carbsOpen === 2" :entries="entries" macro="carbs" :min-shown="0.5" bg="rgba(238, 173, 119, 0.2)" empty-text="Сегодня углеводов в дневнике пока нет" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showCarbs && carbsOpen >= 1">
                  <div @click="freeDetailOpen = !freeDetailOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="freeDetailOpen = !freeDetailOpen">
                    <MacroBar label="Добавленный сахар" :value="daySugars.totals.free" unit="г" :upper="sugarLimit" :norm-text="`до ${sugarLimit} г`" color="#DE8F6A" precise :loading="sugarsLoading || sugarsPending" :approx="daySugars.totals.free_approx" :flush="freeDetailOpen" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="freeDetailOpen" metric="free" macro-label="углеводов" :entries="entries" :values="daySugars.entries" :parts="daySugars.parts" :loading="sugarsLoading" :coverage="daySugars.totals.free_coverage_pct ?? 100" :estimating="daySugars.totals.estimating || 0" :min="0.3" :decimals="1" bg="rgba(222, 143, 106, 0.2)" empty-text="Заметных источников добавленного сахара сегодня нет" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <div v-if="showFiber" @click="fiberDetailsOpen = !fiberDetailsOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="fiberDetailsOpen = !fiberDetailsOpen">
                <MacroBar label="Клетчатка" :value="totals.fiber" unit="г" :upper="NORMS.fiber.upper" :lower="NORMS.fiber.lower" color="#A6B779" :loading="fiberEstimating" :approx="fiberIsApprox" :flush="fiberDetailsOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showFiber && fiberDetailsOpen" :entries="entries" />
              </ExpandTransition>
              <div v-if="showWater" @click="waterDetailsOpen = !waterDetailsOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="waterDetailsOpen = !waterDetailsOpen">
                <MacroBar label="Вода" :value="totals.water" unit="г" :upper="3000" color="#ABDEEB" :no-norm="true" normText="из еды и напитков" :approx="waterIsApprox" :flush="waterDetailsOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showWater && waterDetailsOpen" :entries="entries" macro="water" field="water_ml" :min-shown="5" bg="rgba(171, 222, 235, 0.2)" empty-text="Сегодня воды в дневнике пока нет" integer />
              </ExpandTransition>
            </div>

            <!-- ═══ БЛОК 2: ВИТАМИНЫ И МИНЕРАЛЫ ═══ -->
            <div v-if="showVitamins" class="px-3">
              <VitaminBlock :totals="totals.vitamins" :sex="userSex" :age="userAge" :show-coverage="true" />
            </div>
          </template>

          <!-- ═══ БЛОК 3: ПРИЁМЫ ПИЩИ ═══ -->
          <div v-if="mealGroups.length > 0" class="px-3 mt-8" style="font-family: Inter, sans-serif; font-size: var(--fs-26); font-weight: 600; color: var(--ink); margin-bottom: 4px;">Дневник питания</div>
          <template v-if="mealGroups.length > 0">
            <template v-for="(group, i) in mealGroups" :key="group.id ? 'g' + group.id : 't' + group.entries[0].id">
              <!-- Время с последнего приёма пищи (над самым новым, только "сегодня") -->
              <div v-if="i === 0 && lastMealGap" class="flex items-center gap-2 px-3 mt-[9px] mb-[11px]">
                <div class="meal-divider flex-1" />
                <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14);">{{ lastMealGap }}</span>
                <div class="meal-divider flex-1" />
              </div>

              <div class="mt-2" :ref="el => { if (el) mealGroupRefs[i] = el }">
                <MealGroup

                  :group="group"
                  :kbju-visibility="kbjuVisibility"
                  :fiber-loading="fiberEstimating"
                  :show-hunger="showHunger"
                  :show-weight="showWeight"
                  @add="eatenAt => openAdd(eatenAt)"
                  @updated="onEntryUpdated"
                  @group-updated="onGroupUpdated"
                  @dish-click="onDishClick"
                  @edit-time="onEditTime"
                  @favorite-toggled="onFavoriteToggled"
                />
              </div>

              <!-- Промежуток между приёмами -->
              <div v-if="i < mealGroups.length - 1" class="flex items-center gap-2 px-3 mt-[9px] mb-[11px]">
                <div class="meal-divider flex-1" />
                <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14);">{{ gapLabel(mealGroups[i + 1], group) }}</span>
                <div class="meal-divider flex-1" />
              </div>

              <!-- Промежуток от предыдущего дня (после самого старого приёма) -->
              <div v-if="i === mealGroups.length - 1 && firstGap" class="flex items-center gap-2 px-3 mt-[9px] mb-[11px]">
                <div class="meal-divider flex-1" />
                <span v-if="!firstGap.noText" class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14);">{{ firstGap.label }}</span>
                <div class="meal-divider flex-1" />
              </div>
            </template>
          </template>

          <!-- Ошибка загрузки -->
          <div v-else-if="loadError" class="flex flex-col items-center justify-center pt-16 gap-4 px-6">
            <p style="font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 600; color: var(--ink);">Записи пока не загрузились</p>
            <p style="font-family: Inter, sans-serif; font-size: var(--fs-13); color: var(--ink); opacity: 0.6; text-align: center;">Нет связи с сервером. Всё сохранённое на месте — проверьте соединение и попробуйте ещё раз.</p>
            <button
              @click="loadEntries()"
              class="mt-2 px-6 py-3 rounded-2xl bg-ink text-cream text-15 font-semibold"
              style="font-family: Inter, sans-serif;"
            >Повторить</button>
          </div>

          <!-- Пустой день -->
          <div v-else class="empty-day-container">
            <p class="empty-day-text">{{ authStore.user?.address_form === 'вы' ? 'Добавьте' : 'Добавь' }}<br>первую запись<br>за день</p>
            <div class="empty-day-arrow-wrap">
              <svg width="84" height="177" viewBox="0 0 84 177" fill="none">
                <path d="M82.3681 176.092C82.92 176.072 83.3508 175.608 83.3302 175.056L82.9955 166.062C82.975 165.51 82.5109 165.079 81.959 165.1C81.4071 165.12 80.9764 165.584 80.9969 166.136L81.2944 174.131L73.3 174.428C72.7481 174.449 72.3173 174.913 72.3378 175.465C72.3584 176.017 72.8224 176.447 73.3743 176.427L82.3681 176.092ZM0.990234 0.139557L2.16685e-05 0.279124C5.49104 39.2374 11.6693 69.9325 23.6027 97.4877C35.5454 125.064 53.2276 149.442 81.6506 175.826L82.3309 175.093L83.0112 174.36C54.7501 148.127 37.2504 123.969 25.438 96.6929C13.6164 69.3958 7.46644 38.9226 1.98045 -9.78594e-06L0.990234 0.139557Z" fill="#41291A" opacity="0.35"/>
              </svg>
            </div>
          </div>

          <!-- ═══ БЛОК 4: ОБРАТНАЯ СВЯЗЬ ОТ ЗОИ ═══ -->
          <div v-if="mealGroups.length > 0" class="mt-2 mb-4 bg-cream rounded-2xl px-4 py-3">
            <p style="font-family: Inter, sans-serif; font-size: var(--fs-20); font-weight: 600; color: var(--ink);" class="mb-3">Обратная связь от Зои</p>

            <!-- Кнопка «Переделать» -->
            <button
              v-if="zoyaFeedback && !zoyaLoading"
              @click="loadZoyaFeedback"
              :disabled="!canRedoDayFeedback"
              class="w-full py-2.5 rounded-xl text-13 font-medium disabled:opacity-40"
              style="background: white; color: var(--ink);"
            >
              Переделать обратную связь
            </button>
            <p v-if="zoyaFeedback && !zoyaLoading && !canRedoDayFeedback"
               class="mt-1.5 mb-3 text-12 text-center text-ink/70 leading-snug">
              Дневник не менялся с прошлого разбора — Зоя скажет то же самое
            </p>
            <div v-else-if="zoyaFeedback && !zoyaLoading" class="mb-3"></div>

            <!-- Текст фидбека -->
            <div v-if="zoyaFeedback" class="zoya-feedback" v-html="formatZoyaFeedback(zoyaFeedback)"></div>

            <!-- Загрузка -->
            <div v-if="zoyaLoading" class="flex items-center gap-2 py-4">
              <svg class="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#E5E5E5" stroke-width="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="#41291A" stroke-width="3" stroke-linecap="round"/>
              </svg>
              <span class="text-14 text-ink/70">Зоя анализирует рацион...</span>
            </div>

            <!-- Ошибка -->
            <p v-if="zoyaError" class="text-13 text-error">{{ zoyaError }}</p>

            <!-- Кнопка «Получить анализ» -->
            <button
              v-if="!zoyaFeedback && !zoyaLoading"
              @click="loadZoyaFeedback"
              class="mt-1 w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold active:scale-[0.98] transition-all"
            >
              Получить анализ
            </button>
          </div>

        </template>
      </template>
    </div>

    <!-- Blur fade зона снизу (над NavBar) -->
    <div class="blur-fade-bottom" />

    <!-- FAB «+»: та же кнопка, что камера в AddFoodDialog — цвет, размер и
         место совпадают, чтобы при открытии окна кнопка не прыгала
         (решение владельца 8.10.2026) -->
    <button
      v-if="isDayView"
      data-guide="fab"
      @click="openAdd()"
      class="fab-add fixed flex items-center justify-center
             rounded-full glass-fab
             active:scale-95 transition-transform z-40 select-none"
    >
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FAF3E8" stroke-width="1.5" stroke-linecap="round">
        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
      </svg>
    </button>

    <!-- Диалог добавления -->
    <AddFoodDialog
      v-if="showAdd"
      :date="activeDateStr"
      :eaten-at="addEatenAt"
      :hidden="diaryHidden"
      :restore="addRestore"
      :initial-tab="addInitialTab"
      @close="showAdd = false; addRestore = null; addInitialTab = null"
      @added="onAdded"
      @favorite-toggled="onFavoriteToggled"
    />

    <!-- Незаконченный ввод после перезагрузки или выгрузки приложения
         (services/drafts.js): предлагаем продолжить, а не молча забыть -->
    <Teleport to="body">
      <div v-if="draftOffer" class="fixed inset-0 z-[115] flex items-end justify-center" @click.self="dismissDraft">
        <div class="absolute inset-0 bg-black/40" @click="dismissDraft" />
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-6 pt-6 pb-8 z-10" style="font-family: Inter, sans-serif;">
          <p class="text-17 font-bold text-ink mb-1">Вы не закончили добавлять</p>
          <p class="text-15 text-ink/80 mb-5">«{{ draftTitle(draftOffer) }}». Всё введённое сохранилось. Продолжить?</p>
          <button @click="continueDraft" class="w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold active:scale-[0.98] transition-all">Продолжить</button>
          <button @click="discardDraft" class="w-full py-3 mt-3 rounded-2xl bg-white text-ink text-15 font-semibold">Удалить черновик</button>
        </div>
      </div>
    </Teleport>

    <!-- Диалог редактирования блюда -->
    <PhotoRecognizeDialog
      v-if="showEditDish"
      mode="edit"
      :items="editDishItems"
      :photo-url="editDishPhotoUrl"
      :icons="editDishIcons"
      :dish-name-prop="editDishName"
      :dish-id="editDishId"
      :initial-favorite="editDishFavorite"
      :is-edited-cooking="editDishIsEditedCooking"
      :cooked-weight-g="editDishCookedWeightG"
      :entry-ids="editDishEntryIds"
      :eaten-at="editDishEatenAt"
      :duplicate-rule="editDishDuplicateRule"
      :hidden="diaryHidden"
      :product-photo-custom="editDishProductPhotoCustom"
      @close="showEditDish = false"
      @added="onDishEdited"
      @deleted="onDishDeleted"
      @refresh="loadEntries"
      @favorite-toggled="onFavoriteToggled"
    />

    <!-- Попап дня из периода -->
    <Teleport to="body">
      <div v-if="selectedPeriodDate" v-show="!diaryHidden"
           class="fixed inset-0 z-[45]"
           @click.self="selectedPeriodDate = null">
        <div class="absolute inset-0" @click="selectedPeriodDate = null" />
        <!-- Верхняя граница попапа зависит от вкладки.
             Неделя: сразу под таб-баром. Лента дат лежит поверх (z-46 против
             z-45), и контент, уезжая под неё, просвечивает сквозь размытие —
             как под нижним меню; отступ сверху у скроллящейся области равен
             высоте ленты, чтобы первый экран начинался под ней, а не за ней.
             Месяц: ниже шапки календаря. Там на этой высоте название месяца
             и стрелки переключения, и попап во весь экран их закрывал —
             оставалось пустое место над «Всего за 3 сентября». -->
        <div class="absolute left-0 right-0 bottom-0 flex justify-center"
             style="top: calc(72px + var(--safe-top));">
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl flex flex-col overflow-hidden h-full"
             style="box-shadow: 0 -4px 20px rgba(0,0,0,0.08);">
          <!-- Крестик в «аналитике»: попап занимает всё поле вкладки (решение
               владельца 26.10.2026). В «неделе» сверху остаётся лента дней — закрывает она -->
          <button v-if="!isWeekView" @click="selectedPeriodDate = null" class="absolute right-3 top-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-cream/90 text-ink active:bg-line" aria-label="Закрыть">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <!-- Лоадер -->
          <div v-if="periodDayLoading" class="flex-1 flex justify-center pt-16">
            <div class="w-6 h-6 border-2 border-sand border-t-[#8B7B6B] rounded-full animate-spin" />
          </div>

          <template v-else>
            <div class="flex-1 overflow-y-auto overscroll-contain scrollbar-hide px-3 pb-24"
                 :class="isWeekView ? '' : 'pt-4'" :style="isWeekView ? { paddingTop: (weekStripH + 4) + 'px' } : null">
              <div v-if="isWeekView || !isPeriodDayFuture" :class="isWeekView ? 'week-completeness-slot' : 'day-completeness'">
                <DayStatusToggle v-if="!isPeriodDayFuture" :date="selectedPeriodDate" :kcal="periodDayTotals.kcal" :tz="tz" @changed="onCompletenessChanged" />
              </div>
              <!-- Заголовок -->
              <div class="day-heading">
                <h2>{{ periodDayTitle }}</h2>
              </div>

              <!-- Полоски дня из календаря раскрываются так же, как на экране
                   дня: записи уже загружены тем же GET /diary, а типы жира
                   подгружаются отдельным запросом за выбранную дату -->
              <div v-if="showKcal" @click="pdKcalOpen = !pdKcalOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdKcalOpen = !pdKcalOpen">
                <MacroBar label="Ккал" :value="periodDayTotals.kcal" unit="ккал" :upper="NORMS.kcal.upper" :lower="NORMS.kcal.lower" :lowerLabel="NORMS.kcal.lowerLabel" color="#EE998C" :flush="pdKcalOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showKcal && pdKcalOpen" :entries="periodDayEntries" macro="kcal" :min-shown="5" bg="rgba(238, 153, 140, 0.2)" empty-text="В этот день калорий в дневнике нет" integer />
              </ExpandTransition>
              <div v-if="showProtein" @click="pdProteinOpen = !pdProteinOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdProteinOpen = !pdProteinOpen">
                <MacroBar label="Белки" :value="periodDayTotals.protein" unit="г" :upper="NORMS.protein.upper" :lower="NORMS.protein.lower" color="#9FC2C1" :flush="pdProteinOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showProtein && pdProteinOpen" :entries="periodDayEntries" macro="protein" :min-shown="0.5" bg="rgba(159, 194, 193, 0.2)" empty-text="В этот день белка в дневнике нет" />
              </ExpandTransition>
              <div v-if="showFat" @click="cycleFatsPeriodDay" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleFatsPeriodDay">
                <MacroBar label="Жиры" :value="periodDayTotals.fat" unit="г" :upper="NORMS.fat.upper" :lower="NORMS.fat.lower" color="#EBC06D" :flush="pdFatsOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showFat && pdFatsOpen === 2" :entries="periodDayEntries" macro="fat" :min-shown="0.5" bg="rgba(235, 192, 109, 0.2)" empty-text="В этот день жиров в дневнике нет" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showFat && pdFatsOpen >= 1">
                  <div @click="pdSatOpen = !pdSatOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdSatOpen = !pdSatOpen">
                    <MacroBar label="Насыщенные жиры" :value="periodDayFats.totals.sat" unit="г" :upper="satLimit" :norm-text="`до ${satLimit} г`" color="#d7aa49" precise :loading="periodDayFatsLoading || periodDayFatsPending" :approx="periodDayFats.totals.sat_approx" :flush="pdSatOpen" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="pdSatOpen" metric="sat" :entries="periodDayEntries" :values="periodDayFats.entries" :parts="periodDayFats.parts" :loading="periodDayFatsLoading" :coverage="periodDayFats.totals.sat_coverage_pct ?? 100" :estimating="periodDayFats.totals.estimating || 0" :min="0.3" :decimals="1" bg="rgba(215, 170, 73, 0.2)" empty-text="Заметных источников насыщенных жиров в этот день нет" />
                  </ExpandTransition>
                  <div @click="pdO3Open = !pdO3Open" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdO3Open = !pdO3Open">
                    <MacroBar label="Омега-3" :value="periodDayFats.totals.omega3" unit="г" :upper="O3_NORM.upper" :lower="O3_NORM.lower" color="#eecf8a" precise :loading="periodDayFatsLoading || periodDayFatsPending" :approx="periodDayFats.totals.omega3_approx" :flush="pdO3Open" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="pdO3Open" metric="omega3" :entries="periodDayEntries" :values="periodDayFats.entries" :parts="periodDayFats.parts" :loading="periodDayFatsLoading" :coverage="periodDayFats.totals.omega3_coverage_pct ?? 100" :estimating="periodDayFats.totals.estimating || 0" :min="0.05" :decimals="2" bg="rgba(238, 207, 138, 0.2)" empty-text="Заметных источников омега-3 в этот день нет" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <div v-if="showCarbs" @click="cycleCarbsPeriodDay" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="cycleCarbsPeriodDay">
                <MacroBar label="Углеводы" :value="periodDayTotals.carbs" unit="г" :upper="NORMS.carbs.upper" :lower="NORMS.carbs.lower" color="#EEAD77" :flush="pdCarbsOpen === 2" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showCarbs && pdCarbsOpen === 2" :entries="periodDayEntries" macro="carbs" :min-shown="0.5" bg="rgba(238, 173, 119, 0.2)" empty-text="В этот день углеводов в дневнике нет" />
              </ExpandTransition>
              <ExpandTransition>
                <div v-if="showCarbs && pdCarbsOpen >= 1">
                  <div @click="pdFreeOpen = !pdFreeOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdFreeOpen = !pdFreeOpen">
                    <MacroBar label="Добавленный сахар" :value="periodDaySugars.totals.free" unit="г" :upper="sugarLimit" :norm-text="`до ${sugarLimit} г`" color="#DE8F6A" precise :loading="periodDaySugarsLoading || periodDaySugarsPending" :approx="periodDaySugars.totals.free_approx" :flush="pdFreeOpen" />
                  </div>
                  <ExpandTransition>
                    <FatTypeBlock v-if="pdFreeOpen" metric="free" macro-label="углеводов" :entries="periodDayEntries" :values="periodDaySugars.entries" :parts="periodDaySugars.parts" :loading="periodDaySugarsLoading" :coverage="periodDaySugars.totals.free_coverage_pct ?? 100" :estimating="periodDaySugars.totals.estimating || 0" :min="0.3" :decimals="1" bg="rgba(222, 143, 106, 0.2)" empty-text="Заметных источников добавленного сахара в этот день нет" />
                  </ExpandTransition>
                </div>
              </ExpandTransition>
              <div v-if="showFiber" @click="pdFiberOpen = !pdFiberOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdFiberOpen = !pdFiberOpen">
                <MacroBar label="Клетчатка" :value="periodDayTotals.fiber" unit="г" :upper="NORMS.fiber.upper" :lower="NORMS.fiber.lower" color="#A6B779" :approx="periodDayFiberApprox" :flush="pdFiberOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showFiber && pdFiberOpen" :entries="periodDayEntries" empty-text="В этот день клетчатки в дневнике нет" />
              </ExpandTransition>
              <div v-if="showWater" @click="pdWaterOpen = !pdWaterOpen" class="cursor-pointer" role="button" tabindex="0" @keydown.enter.space.prevent="pdWaterOpen = !pdWaterOpen">
                <MacroBar label="Вода" :value="periodDayTotals.water" unit="г" :upper="3000" color="#ABDEEB" :no-norm="true" normText="из еды и напитков" :approx="periodDayWaterApprox" :flush="pdWaterOpen" />
              </div>
              <ExpandTransition>
                <MacroSourcesBlock v-if="showWater && pdWaterOpen" :entries="periodDayEntries" macro="water" field="water_ml" :min-shown="5" bg="rgba(171, 222, 235, 0.2)" empty-text="В этот день воды в дневнике нет" integer />
              </ExpandTransition>

              <!-- Витамины -->
              <VitaminBlock v-if="showVitamins" :totals="periodDayTotals.vitamins" :sex="userSex" :age="userAge" :show-coverage="true" />

              <!-- Приёмы пищи -->
              <template v-if="periodDayMealGroups.length > 0">
                <div class="mt-8" style="font-family: Inter, sans-serif; font-size: var(--fs-26); font-weight: 600; color: var(--ink); margin-bottom: 4px;">Дневник питания</div>
                <template v-for="(group, i) in periodDayMealGroups" :key="group.id ? 'pg' + group.id : 'pt' + group.entries[0].id">
                  <!-- -mx-3: у MealGroup свой px-3, а область попапа уже с px-3 —
                       иначе приём отступал на 12 px правее заголовка (28.10.2026) -->
                  <div class="mt-2 -mx-3">
                    <MealGroup
                      :group="group"
                      :kbju-visibility="kbjuVisibility"
                      :fiber-loading="fiberEstimating"
                      :show-hunger="showHunger"
                      :show-weight="showWeight"
                      @add="eatenAt => openAdd(eatenAt)"
                      @updated="onEntryUpdated"
                      @group-updated="onGroupUpdated"
                      @dish-click="onDishClick"
                      @edit-time="onEditTime"
                      @favorite-toggled="onFavoriteToggled"
                    />
                  </div>
                  <!-- Промежуток между приёмами -->
                  <div v-if="i < periodDayMealGroups.length - 1" class="flex items-center gap-2 mt-[9px] mb-[11px]">
                    <div class="meal-divider flex-1" />
                    <span class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14);">{{ periodDayGapLabel(periodDayMealGroups[i + 1], group) }}</span>
                    <div class="meal-divider flex-1" />
                  </div>
                  <!-- Промежуток от предыдущего дня (после самого старого приёма) -->
                  <div v-if="i === periodDayMealGroups.length - 1 && periodDayFirstGap" class="flex items-center gap-2 mt-[9px] mb-[11px]">
                    <div class="meal-divider flex-1" />
                    <span v-if="!periodDayFirstGap.noText" class="text-ink" style="font-family: Inter, sans-serif; font-size: var(--fs-14);">{{ periodDayFirstGap.label }}</span>
                    <div class="meal-divider flex-1" />
                  </div>
                </template>
              </template>

              <!-- Пустой день -->
              <div v-else class="flex flex-col items-center justify-center pt-12 gap-4">
                <p class="text-16 font-semibold text-ink/70">Записей нет</p>
              </div>

              <!-- Фидбэк Зои за этот день -->
              <div v-if="periodDayFeedback" class="mt-6">
                <div style="font-family: Inter, sans-serif; font-size: var(--fs-20); font-weight: 600; color: var(--ink); margin-bottom: 8px;">Обратная связь от Зои</div>
                <div class="zoya-feedback" v-html="formatZoyaFeedback(periodDayFeedback)"></div>
              </div>
            </div>
          </template>
        </div>
        </div>
      </div>
    </Teleport>

    <!-- Time Picker Popup -->
    <TimePickerPopup
      v-if="showTimePicker && timePickerData"
      :key="timePickerMode"
      :date="timePickerMode === 'copy' ? copyMealDate : timePickerData.date"
      :title="timePickerMode === 'copy' ? 'Скопировать приём пищи' : 'Дата и время приёма'"
      :description="timePickerMode === 'copy' ? `Блюд в копии: ${timePickerData.dishCount || 1}. Исходный приём сохранится. Копию можно будет изменить.` : ''"
      :copy-label="timePickerMode === 'edit' ? 'Скопировать на другую дату' : ''"
      :confirm-label="timePickerMode === 'copy' ? 'Скопировать приём пищи' : 'Сохранить'"
      :show-year="timePickerMode === 'copy'"
      :busy="timePickerBusy"
      :error="timePickerError"
      :start-hour="timePickerData.startHour"
      :start-min="timePickerData.startMin"
      :end-hour="timePickerData.endHour"
      :end-min="timePickerData.endMin"
      :show-repeat="timePickerMode === 'edit'"
      repeat-label="Повторить приём пищи"
      :repeat="timePickerRepeatView"
      @confirm="onTimePickerConfirm"
      @copy="onCopyMeal"
      @close="closeTimePicker"
    />

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, onActivated, onDeactivated, nextTick, reactive } from 'vue'
import { useRouter } from 'vue-router'
import api from '../services/api.js'
import { uuid } from '../utils/uuid.js'
import { cacheGet, cacheSet, cacheDel, cacheClean, cacheClearFutureDiary } from '../services/cache.js'
import { onReconnect, offline } from '../services/netStatus.js'
import { pendingDrafts, draftTitle, clearDraft } from '../services/drafts.js'
import { useAuthStore } from '../store/auth.js'
import AddFoodDialog         from '../components/AddFoodDialog.vue'
import PhotoRecognizeDialog  from '../components/PhotoRecognizeDialog.vue'
import MacroBar              from '../components/MacroBar.vue'
import DayStatusToggle from '../components/DayStatusToggle.vue'
import { cardCache as reportCardCache } from '../utils/weekReport.js'
import VitaminBlock          from '../components/VitaminBlock.vue'
import MacroSourcesBlock     from '../components/MacroSourcesBlock.vue'
import FatTypeBlock          from '../components/FatTypeBlock.vue'
import MacroPeriodBlock      from '../components/MacroPeriodBlock.vue'
import ExpandTransition      from '../components/ExpandTransition.vue'
import MealGroup             from '../components/MealGroup.vue'
import FirstStepsCard        from '../components/FirstStepsCard.vue'
import guide, { state as guideState, active as guideActive, checklistVisible as guideChecklistVisible } from '../services/guide.js'
import TimePickerPopup       from '../components/TimePickerPopup.vue'
import { planRuleChange, deriveRuleView, effectiveAnchorDate, todayDateStr, todayStartIso } from '../utils/repeatRule.js'
import { mergeEntryUnits } from '../utils/units.js'
import WeekDayPicker         from '../components/WeekDayPicker.vue'
import WeekReportCard        from '../components/report/WeekReportCard.vue'
import MonthCalendar         from '../components/MonthCalendar.vue'
import { demoPeriod } from '../utils/periodDemo.js'
import DayBars               from '../components/DayBars.vue'
import PeriodMetricCard      from '../components/PeriodMetricCard.vue'
import { COLORS } from '../utils/weekReport.js'
import WeightTrendChart      from '../components/WeightTrendChart.vue'
import { addDays as addDaysYmd, daysBetween, bucketSeries, daysInNorm, weightTrend, weeklyRate, rangeLabel, shortRange, lastStreak, defaultRange, shortDate as shortDateOf, dayWord as dayWordOf } from '../utils/periodCharts.js'

defineOptions({ name: 'Diary' })

const authStore = useAuthStore()
const router = useRouter()

// ── SVG иконки для закладок ──────────────────────────────────────────────────

const svgMonth = `<svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 3.2695C1.6193 3.2695 0.5 4.3888 0.5 5.7695V22.0003C0.5 23.381 1.6193 24.5003 3 24.5003H22C23.3807 24.5003 24.5 23.381 24.5 22.0003V5.7695C24.5 4.3888 23.3807 3.2695 22 3.2695H18.9615" stroke="#41291A" stroke-linecap="round" stroke-linejoin="round"/><path d="M0.5 9.73047H24.5" stroke="#41291A" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.03711 0.5V6.03846" stroke="#41291A" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.9629 0.5V6.03846" stroke="#41291A" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.03711 3.26953H15.2679" stroke="#41291A" stroke-linecap="round" stroke-linejoin="round"/></svg>`

// Вкладка «аналитика»: столбики без подписи — подпись дублировала заголовок (владелец 27.10.2026)
const svgAnalytics = `<svg width="30" height="25" viewBox="0 0 34 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 4.5H7C8.38071 4.5 9.5 5.61929 9.5 7V25C9.5 26.3807 8.38071 27.5 7 27.5H3C1.61929 27.5 0.5 26.3807 0.5 25V7C0.5 5.61929 1.61929 4.5 3 4.5Z" stroke="#41291A"/><path d="M15 8.5H19C20.3807 8.5 21.5 9.61929 21.5 11V25C21.5 26.3807 20.3807 27.5 19 27.5H15C13.6193 27.5 12.5 26.3807 12.5 25V11C12.5 9.61929 13.6193 8.5 15 8.5Z" stroke="#41291A"/><path d="M27 0.5H31C32.3807 0.5 33.5 1.61929 33.5 3V25C33.5 26.3807 32.3807 27.5 31 27.5H27C25.6193 27.5 24.5 26.3807 24.5 25V3C24.5 1.61929 25.6193 0.5 27 0.5Z" stroke="#41291A"/></svg>`

const svgWeek = `<svg width="43" height="22" viewBox="0 0 43 22" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="9.5" y="0.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="0.5" y="12.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="9.5" y="12.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="18.5" y="0.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="27.5" y="0.5" width="6" height="9" rx="2" stroke="#41291A"/><rect x="36.5" y="0.5" width="6" height="9" rx="2" stroke="#41291A"/></svg>`

const showKcalColors = computed(() => showKcal.value && (authStore.user?.diary_show_kcal_colors ?? true))

// ── Нормы КБЖУ (персональные или дефолтные) ──────────────────────────────────
const showDeficitMark = computed(() => !!authStore.user?.show_deficit_mark)

const effectiveKcalNorm = computed(() => {
  const u = authStore.user
  return u?.custom_kcal_norm || u?.kcal_intake_norm || null
})

const effectiveDeficitPct = computed(() => {
  const u = authStore.user
  return u?.custom_deficit_pct != null ? u.custom_deficit_pct : 0
})

const NORMS = computed(() => {
  const u = authStore.user
  const kcalNorm = effectiveKcalNorm.value
  const deficitPct = effectiveDeficitPct.value
  const showLower = showDeficitMark.value && deficitPct !== 0
  if (kcalNorm) {
    return {
      kcal:    {
        upper: kcalNorm,
        lower: showLower ? Math.round(kcalNorm * (1 + deficitPct / 100)) : null,
        lowerLabel: showLower ? `${deficitPct}%` : null,
      },
      protein: { lower: u?.custom_protein_norm_min || u?.protein_norm_min, upper: u?.custom_protein_norm_max || u?.protein_norm_max },
      fat:     { lower: u?.custom_fat_norm_min || u?.fat_norm_min,     upper: u?.custom_fat_norm_max || u?.fat_norm_max },
      carbs:   { lower: u?.custom_carbs_norm_min || u?.carbs_norm_min,   upper: u?.custom_carbs_norm_max || u?.carbs_norm_max },
      fiber:   { lower: 25, upper: 35 },
    }
  }
  return {
    kcal:    { upper: 2000, lower: showLower ? Math.round(2000 * (1 + deficitPct / 100)) : null, lowerLabel: showLower ? `${deficitPct}%` : null },
    protein: { lower: 80,  upper: 120 },
    fat:     { lower: 50,  upper: 100 },
    carbs:   { lower: 200, upper: 300 },
    fiber:   { lower: 25,  upper: 35 },
  }
})

// ── Даты ──────────────────────────────────────────────────────────────────────
const today     = ref(new Date())
const yesterday = computed(() => { const d = new Date(today.value); d.setDate(d.getDate() - 1); return d })

const MONTHS_SHORT = ['янв', 'фев', 'март', 'апр', 'май', 'июнь', 'июль', 'авг', 'сен', 'окт', 'ноя', 'дек']
const toDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

// «Период» (с 19.10.2026) заменил «месяц» и «год+»: любой диапазон дат с
// графиками, по умолчанию весь дневник. Выбор дат — полями под заголовком и на календаре,
// календарь на вкладке по-прежнему открывает день.
const tabs = computed(() => [
  { key: 'period',    label: 'графики',  svg: svgAnalytics, dayDate: null },
  { key: 'week',      label: 'неделя',  svg: svgWeek,  dayDate: null },
  { key: 'yesterday', dayDate: yesterday.value.getDate(), monthLabel: MONTHS_SHORT[yesterday.value.getMonth()] },
  { key: 'today',     dayDate: today.value.getDate(),     monthLabel: MONTHS_SHORT[today.value.getMonth()] },
])

const activeTab     = ref('today')
const isDayView     = computed(() => activeTab.value === 'today' || activeTab.value === 'yesterday')
const activeDateStr = computed(() =>
  activeTab.value === 'yesterday' ? toDateStr(yesterday.value) : toDateStr(today.value)
)

const isWeekView  = computed(() => activeTab.value === 'week')
const isMonthView = computed(() => activeTab.value === 'period')
const isPeriodView = computed(() => isWeekView.value || isMonthView.value)

const userSex = computed(() => authStore.user?.sex || 'female')  // пол не указан → женский
const userAge = computed(() => {
  const bd = authStore.user?.birth_date
  if (!bd) return null
  return Math.floor((Date.now() - new Date(bd).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
})
// ── Granular diary visibility from diary_settings ──
const ds = computed(() => {
  const defaults = { show_kcal: true, show_protein: true, show_fat: true, show_carbs: true, show_fiber: true, show_water: true, show_vitamins: true, show_hunger: false }
  return { ...defaults, ...(authStore.user?.diary_settings || {}) }
})
const showKcal     = computed(() => ds.value.show_kcal)
const showProtein  = computed(() => ds.value.show_protein)
const showFat      = computed(() => ds.value.show_fat)
const showCarbs    = computed(() => ds.value.show_carbs)
const showFiber    = computed(() => ds.value.show_fiber)
const showWater    = computed(() => ds.value.show_water)
const showVitamins = computed(() => ds.value.show_vitamins)
const showHunger   = computed(() => ds.value.show_hunger)
const showWeight   = computed(() => ds.value.show_weight ?? true)
const anyMacroVisible = computed(() => showKcal.value || showProtein.value || showFat.value || showCarbs.value || showFiber.value || showWater.value)
// Обращение на ты/вы — как в остальных надписях дневника
const isVy = computed(() => authStore.user?.address_form === 'вы')

const kbjuVisibility = computed(() => ({ kcal: showKcal.value, protein: showProtein.value, fat: showFat.value, carbs: showCarbs.value, fiber: showFiber.value }))

// ── Данные ────────────────────────────────────────────────────────────────────
const entries = ref([])
const prevLastEatenAt = ref(null)
// Мета приёмов пищи из zoya_meal_groups: { [groupId]: { note, hunger_before, ... } }
const mealGroupMeta = ref({})

function indexGroups(list) {
  const byId = {}
  for (const g of list || []) byId[g.id] = g
  return byId
}
const loading = ref(false)
const loadError = ref(false)
const isEmptyDay = computed(() => isDayView.value && !loading.value && !loadError.value && mealGroups.value.length === 0)
const showAdd = ref(false)
// Черновик, который AddFoodDialog должен продолжить (см. шторку выше)
const addRestore = ref(null)
const draftOffer = ref(null)
function offerDraft() {
  if (!authStore.user || showAdd.value || showEditDish.value) return
  const [first] = pendingDrafts()
  draftOffer.value = first || null
}
function continueDraft() {
  const d = draftOffer.value
  draftOffer.value = null
  if (!d) return
  addRestore.value = d
  showAdd.value = true
}
function discardDraft() {
  if (draftOffer.value) clearDraft(draftOffer.value.kind)
  draftOffer.value = null
}
// Закрыть вопрос без ответа — черновик остаётся до следующего запуска
function dismissDraft() { draftOffer.value = null }
const diaryHidden = ref(false)
const addEatenAt = ref(null)
const mealGroupRefs = ref({})
const tz      = Intl.DateTimeFormat().resolvedOptions().timeZone

// ── Клетчатка: дооценка по запросу ─────────────────────────────────────────
// Оценивается только то, что человек видит сейчас и по чему данных нет.
// Фоновой прогонки по всей базе нет намеренно: продуктов без измеренной
// клетчатки сотни тысяч, а в дневниках встречается меньше тысячи.
const fiberEstimating = ref(false)
// Раскладка «откуда клетчатка» — раскрывается тапом по самой полоске
const fiberDetailsOpen = ref(false)
const waterDetailsOpen = ref(false)
// Жиры раскрываются в два шага: 0 — свернуто, 1 — видны полоски
// «Насыщенные»/«Омега-3», 2 — плюс раскладка общего жира
const fatsOpen = ref(0)
const satDetailOpen = ref(false)
const o3DetailOpen = ref(false)
// Углеводы раскрываются в два шага, как жиры: 0 — свернуто, 1 — видна
// полоска «Добавленный сахар», 2 — плюс раскладка общих углеводов
const carbsOpen = ref(0)
const freeDetailOpen = ref(false)
const carbsPeriodOpen = ref(0)
const freePeriodOpen = ref(false)
function cycleCarbsDay() {
  carbsOpen.value = (carbsOpen.value + 1) % 3
  if (carbsOpen.value === 0) freeDetailOpen.value = false
}
function cycleCarbsPeriod() {
  carbsPeriodOpen.value = (carbsPeriodOpen.value + 1) % 3
  if (carbsPeriodOpen.value === 0) freePeriodOpen.value = false
}
const fatsPeriodOpen = ref(0)
const satPeriodOpen = ref(false)
const o3PeriodOpen = ref(false)

function cycleFatsDay() {
  fatsOpen.value = (fatsOpen.value + 1) % 3
  if (fatsOpen.value === 0) { satDetailOpen.value = false; o3DetailOpen.value = false }
}
function cycleFatsPeriod() {
  fatsPeriodOpen.value = (fatsPeriodOpen.value + 1) % 3
  if (fatsPeriodOpen.value === 0) { satPeriodOpen.value = false; o3PeriodOpen.value = false }
}

// Личный лимит насыщенных; 22 г — ориентир ВОЗ (<10% при 2000 ккал),
// пока нормы не рассчитаны. Омега-3 — мягкий справочный ориентир 1–2 г.
const satLimit = computed(() => authStore.user?.saturated_fat_limit || 22)
const O3_NORM = { lower: 1, upper: 2 }

// Типы жира за день считает сервер — GET /diary/fats (resolveFats по
// карточкам, блюда без карточки — через ингредиенты раскладки)
const EMPTY_DAY_FATS = { totals: { sat: 0, omega3: 0 }, entries: {}, parts: {} }
const dayFats = ref(EMPTY_DAY_FATS)
const fatsLoading = ref(false)

let fatsRefetchTimer = null

// Пока часть карточек дня ещё оценивается, полоски подтипов (насыщенные,
// омега-3, добавленный сахар) показывают лоадер, а не промежуточную цифру:
// число без части продуктов читалось бы как итог. Перезапрос — каждые 15 с,
// но не дольше минуты: сорвавшаяся оценка оставляет карточки без отметки, и
// без потолка полоска дёргала бы модель бесконечно.
const TYPE_POLL_MS = 15000
const TYPE_POLL_MAX = 4
const fatsPending = ref(false)
let fatsPollLeft = 0

async function loadDayFats() {
  const date = activeDateStr.value
  fatsLoading.value = true
  try {
    const { data } = await api.get('/diary/fats', { params: { date, tz } })
    if (activeDateStr.value !== date) return
    dayFats.value = data
    clearTimeout(fatsRefetchTimer)
    if (data?.totals?.estimating > 0 && fatsPollLeft > 0) {
      fatsPollLeft--
      fatsPending.value = true
      fatsRefetchTimer = setTimeout(() => {
        if (fatsOpen.value >= 1 && isDayView.value && activeDateStr.value === date) loadDayFats()
      }, TYPE_POLL_MS)
    } else {
      fatsPending.value = false
    }
  } catch (err) {
    fatsPending.value = false
    console.error('Day fats error:', err.response?.data?.error || err.message)
  } finally {
    if (activeDateStr.value === date) fatsLoading.value = false
  }
}

watch([fatsOpen, activeDateStr], ([open]) => {
  if (open >= 1 && isDayView.value) { fatsPollLeft = TYPE_POLL_MAX; loadDayFats() }
  else if (!open) { dayFats.value = EMPTY_DAY_FATS; fatsPending.value = false; clearTimeout(fatsRefetchTimer) }
})
// Добавление или правка еды меняет записи — раскрытые полоски пересчитываем
watch(entries, () => {
  if (fatsOpen.value >= 1 && isDayView.value) loadDayFats()
  if (carbsOpen.value >= 1 && isDayView.value) loadDaySugars()
})

// Личный лимит добавленного сахара; 50 г — ориентир ВОЗ (<10 % при 2000
// ккал), пока нормы не рассчитаны. Считает recalcNorms как added_sugar_limit.
const sugarLimit = computed(() => authStore.user?.added_sugar_limit || 50)

// Сахара за день считает сервер — GET /diary/sugars, той же схемой, что жиры
const EMPTY_DAY_SUGARS = { totals: { total: 0, free: 0 }, entries: {}, parts: {} }
const daySugars = ref(EMPTY_DAY_SUGARS)
const sugarsLoading = ref(false)
// Пока часть карточек дня ещё оценивается, полоска показывает лоадер, а не
// промежуточную цифру (просьба владельца 29.09.2026): число без части
// продуктов читалось бы как итог. Перезапрос — каждые 15 с, но не дольше
// минуты: если оценка сорвалась, карточки остаются без отметки, и без
// потолка полоска дёргала бы модель бесконечно.
const sugarsPending = ref(false)
let sugarsPollLeft = 0
let sugarsRefetchTimer = null

async function loadDaySugars() {
  const date = activeDateStr.value
  sugarsLoading.value = true
  try {
    const { data } = await api.get('/diary/sugars', { params: { date, tz } })
    if (activeDateStr.value !== date) return
    daySugars.value = data
    clearTimeout(sugarsRefetchTimer)
    if (data?.totals?.estimating > 0 && sugarsPollLeft > 0) {
      sugarsPollLeft--
      sugarsPending.value = true
      sugarsRefetchTimer = setTimeout(() => {
        if (carbsOpen.value >= 1 && isDayView.value && activeDateStr.value === date) loadDaySugars()
      }, TYPE_POLL_MS)
    } else {
      sugarsPending.value = false
    }
  } catch (err) {
    sugarsPending.value = false
    console.error('Day sugars error:', err.response?.data?.error || err.message)
  } finally {
    if (activeDateStr.value === date) sugarsLoading.value = false
  }
}

watch([carbsOpen, activeDateStr], ([open]) => {
  if (open >= 1 && isDayView.value) { sugarsPollLeft = TYPE_POLL_MAX; loadDaySugars() }
  else if (!open) { daySugars.value = EMPTY_DAY_SUGARS; sugarsPending.value = false; clearTimeout(sugarsRefetchTimer) }
})
const proteinDetailsOpen = ref(false)
const proteinPeriodOpen = ref(false)
const kcalDetailsOpen = ref(false)
const kcalPeriodOpen = ref(false)
// Раскладка на неделе и месяце — своя, потому что данные приходят с сервера
const fiberPeriodOpen = ref(false)
const waterPeriodOpen = ref(false)
// Заголовок блока называет рамку явно: полоска показывает среднее за день,
// а список — суммы за период, и без этого числа выглядели бы несходящимися.
const fiberPeriodDays = computed(() => periodData.value?.days_tracked || 0)
// Записи, по которым уже просили оценку в этой сессии. Ответ сервера
// проставляет fiber_estimated_at, поэтому со следующего GET флаг придёт
// снятым; набор защищает только от повторного запроса по кэшу.
const fiberAsked = new Set()

// В сумму дня вошла оценка, а не только измерения — показываем ~
const fiberIsApprox = computed(() => entries.value.some(e => e.fiber_source === 'ai'))
// Вода: «~», если хоть одна запись дня посчитана по массовому балансу, а не
// измерена (water_source ставит сервер, см. lib/water.js)
const waterIsApprox = computed(() => entries.value.some(e => e.water_source === 'balance' || e.water_source === 'ai'))
const periodDayWaterApprox = computed(() => periodDayEntries.value.some(e => e.water_source === 'balance' || e.water_source === 'ai'))
const periodDayFiberApprox = computed(() => periodDayEntries.value.some(e => e.fiber_source === 'ai'))

// Раскрытия полосок в попапе дня из календаря недели/месяца. Состояния свои,
// а не дневные: попап открывается поверх дня, и общие флаги раскрывали бы
// обе панели разом. Сбрасываются при выборе другой даты — раскладка
// прошлого дня не должна переезжать на новый.
const pdKcalOpen = ref(false)
const pdProteinOpen = ref(false)
const pdFatsOpen = ref(0)
const pdSatOpen = ref(false)
const pdO3Open = ref(false)
const pdCarbsOpen = ref(0)
const pdFreeOpen = ref(false)
function cycleCarbsPeriodDay() {
  pdCarbsOpen.value = (pdCarbsOpen.value + 1) % 3
  if (pdCarbsOpen.value === 0) pdFreeOpen.value = false
}
const pdFiberOpen = ref(false)
const pdWaterOpen = ref(false)
function cycleFatsPeriodDay() {
  pdFatsOpen.value = (pdFatsOpen.value + 1) % 3
  if (pdFatsOpen.value === 0) { pdSatOpen.value = false; pdO3Open.value = false }
}
function resetPeriodDayDetails() {
  pdKcalOpen.value = false; pdProteinOpen.value = false; pdFatsOpen.value = 0
  pdSatOpen.value = false; pdO3Open.value = false; pdCarbsOpen.value = 0; pdFreeOpen.value = false
  pdFiberOpen.value = false; pdWaterOpen.value = false
  periodDayFats.value = EMPTY_DAY_FATS; periodDayFatsPending.value = false; clearTimeout(periodDayFatsTimer)
  periodDaySugars.value = EMPTY_DAY_SUGARS; periodDaySugarsPending.value = false; clearTimeout(periodDaySugarsTimer)
}
// Типы жира для дня попапа — тот же GET /diary/fats, но за выбранную дату;
// loadDayFats() привязан к активному дню и сюда не подходит
const periodDayFats = ref(EMPTY_DAY_FATS)
const periodDayFatsLoading = ref(false)
const periodDayFatsPending = ref(false)
let periodDayFatsPollLeft = 0
let periodDayFatsTimer = null
async function loadPeriodDayFats() {
  const date = selectedPeriodDate.value
  if (!date) return
  periodDayFatsLoading.value = true
  try {
    const { data } = await api.get('/diary/fats', { params: { date, tz } })
    if (selectedPeriodDate.value !== date) return
    periodDayFats.value = data
    clearTimeout(periodDayFatsTimer)
    if (data?.totals?.estimating > 0 && periodDayFatsPollLeft > 0) {
      periodDayFatsPollLeft--
      periodDayFatsPending.value = true
      periodDayFatsTimer = setTimeout(() => {
        if (pdFatsOpen.value >= 1 && selectedPeriodDate.value === date) loadPeriodDayFats()
      }, TYPE_POLL_MS)
    } else {
      periodDayFatsPending.value = false
    }
  } catch (err) {
    periodDayFatsPending.value = false
    console.error('Period day fats error:', err.response?.data?.error || err.message)
  } finally {
    if (selectedPeriodDate.value === date) periodDayFatsLoading.value = false
  }
}
watch(pdFatsOpen, (open) => {
  if (open >= 1) { periodDayFatsPollLeft = TYPE_POLL_MAX; loadPeriodDayFats() }
  else { periodDayFats.value = EMPTY_DAY_FATS; periodDayFatsPending.value = false; clearTimeout(periodDayFatsTimer) }
})
// Сахара для дня попапа — GET /diary/sugars за выбранную дату
const periodDaySugars = ref(EMPTY_DAY_SUGARS)
const periodDaySugarsLoading = ref(false)
const periodDaySugarsPending = ref(false)
let periodDaySugarsPollLeft = 0
let periodDaySugarsTimer = null
async function loadPeriodDaySugars() {
  const date = selectedPeriodDate.value
  if (!date) return
  periodDaySugarsLoading.value = true
  try {
    const { data } = await api.get('/diary/sugars', { params: { date, tz } })
    if (selectedPeriodDate.value !== date) return
    periodDaySugars.value = data
    clearTimeout(periodDaySugarsTimer)
    if (data?.totals?.estimating > 0 && periodDaySugarsPollLeft > 0) {
      periodDaySugarsPollLeft--
      periodDaySugarsPending.value = true
      periodDaySugarsTimer = setTimeout(() => {
        if (pdCarbsOpen.value >= 1 && selectedPeriodDate.value === date) loadPeriodDaySugars()
      }, TYPE_POLL_MS)
    } else {
      periodDaySugarsPending.value = false
    }
  } catch (err) {
    periodDaySugarsPending.value = false
    console.error('Period day sugars error:', err.response?.data?.error || err.message)
  } finally {
    if (selectedPeriodDate.value === date) periodDaySugarsLoading.value = false
  }
}
watch(pdCarbsOpen, (open) => {
  if (open >= 1) { periodDaySugarsPollLeft = TYPE_POLL_MAX; loadPeriodDaySugars() }
  else { periodDaySugars.value = EMPTY_DAY_SUGARS; periodDaySugarsPending.value = false; clearTimeout(periodDaySugarsTimer) }
})

async function ensureFiberEstimates(date) {
  const pending = entries.value.filter(e => e.fiber_pending && !fiberAsked.has(e.id))
  if (!pending.length) return

  pending.forEach(e => fiberAsked.add(e.id))
  fiberEstimating.value = true
  try {
    const { data } = await api.post('/diary/estimate-fiber', { entry_ids: pending.map(e => e.id) })
    // Пока считали, пользователь мог уйти на другой день
    if (activeDateStr.value !== date) return
    const got = data?.entries || {}
    const asked = new Set(pending.map(e => e.id))
    entries.value = entries.value.map(e => {
      const v = got[e.id]
      if (v) return { ...e, fiber: v.fiber, fiber_source: v.fiber_source, fiber_pending: false }
      // Спросили, а значения не пришло: у продукта его нет ни в одном
      // источнике, и модель тоже не смогла. Флаг снимаем — иначе запись
      // осталась бы «вечно считающейся» до перезагрузки дня.
      return asked.has(e.id) ? { ...e, fiber_pending: false } : e
    })
  } catch {
    // Молча: клетчатка не тот показатель, из-за которого стоит ронять экран.
    // Но из списка спрошенных убираем: сбой сети не повод больше не пытаться.
    pending.forEach(e => fiberAsked.delete(e.id))
  } finally {
    fiberEstimating.value = false
  }
}

async function loadEntries() {
  if (!isDayView.value) return
  if (!authStore.user) { entries.value = []; return }
  const date = activeDateStr.value
  const ck = `diary:${date}`
  loadError.value = false

  // Stale-while-revalidate: show cached data immediately
  const cached = cacheGet(ck)
  if (cached) {
    entries.value = cached.entries || []
    mealGroupMeta.value = indexGroups(cached.groups)
    prevLastEatenAt.value = cached.prev_last_eaten_at || null
    loading.value = false
  } else {
    entries.value = []
    mealGroupMeta.value = {}
    prevLastEatenAt.value = null
    loading.value = true
  }

  // Fetch fresh data from network in background
  try {
    const { data } = await api.get('/diary', { params: { date, tz } })
    // Защита от гонки: пока шёл запрос, пользователь мог переключить таб.
    // Если дата изменилась — не трогаем entries.value, только обновляем кэш.
    const stale = activeDateStr.value !== date
    // Merge: если за время fetch кэш обновился (оптимистичный emit из MealGroup),
    // перенести note/hunger/satiety из кэша в полученные данные,
    // чтобы не перезатереть локальные правки устаревшим ответом сервера.
    const freshCache = cacheGet(ck)
    if (freshCache?.entries?.length && data.entries?.length) {
      const cById = {}
      for (const e of freshCache.entries) cById[e.id] = e
      for (const e of data.entries) {
        const c = cById[e.id]
        if (!c) continue
        if (c.note && !e.note)                   e.note           = c.note
        if (c.hunger_before && !e.hunger_before) e.hunger_before  = c.hunger_before
        if (c.satiety_after && !e.satiety_after) e.satiety_after  = c.satiety_after
      }
    }
    // То же для мета приёмов пищи: локальная правка не должна теряться,
    // если ответ сервера пришёл раньше, чем доехал PATCH.
    if (freshCache?.groups?.length && data.groups?.length) {
      const cById = indexGroups(freshCache.groups)
      for (const g of data.groups) {
        const c = cById[g.id]
        if (!c) continue
        if (c.note && !g.note)                   g.note          = c.note
        if (c.hunger_before && !g.hunger_before) g.hunger_before = c.hunger_before
        if (c.satiety_after && !g.satiety_after) g.satiety_after = c.satiety_after
      }
    }
    const isToday = date === toDateStr(today.value)
    lastLoadedAt = Date.now()
    cacheSet(ck, data, isToday ? 10 * 60 * 1000 : 24 * 3600 * 1000)
    if (!stale) {
      entries.value = data.entries || []
      mealGroupMeta.value = indexGroups(data.groups)
      prevLastEatenAt.value = data.prev_last_eaten_at || null
      loadSavedFeedback()
      ensureFiberEstimates(date)
    }
  } catch {
    // If we already showed cached data, keep it — no error
    if (!cached) {
      loadError.value = true
    }
  } finally {
    loading.value = false
  }
}

watch(activeTab, () => {
  selectedPeriodDate.value = null  // Закрыть попап при смене таба
  cancelPick(); menuOpen.value = false
  if (isDayView.value) loadEntries()
  else if (isPeriodView.value) loadPeriodData()
})

// Перезагрузка данных при (повторной) авторизации — компонент уже смонтирован,
// но user/token мог быть null на момент первого loadEntries()
watch(() => authStore.token, (newToken) => {
  if (newToken) {
    trackedLoaded.value = false; rangeFrom.value = null; rangeTo.value = null; isDemo.value = false
    if (isDayView.value) loadEntries()
    else if (isPeriodView.value) loadPeriodData()
  }
})

// Живое время для разделителя "сколько прошло с последнего приёма"
const now = ref(new Date())
let nowTimer = null

let currentDateStr = toDateStr(today.value)

// Самовосстановление. Вернулись на экран или появилась связь — перечитываем
// день, если он старше 30 секунд. Потерянный ответ сохранения (iOS-PWA:
// сервер записал, приложение ответа не увидело) тогда не оставляет дневник
// пустым до ручной перезагрузки.
let lastLoadedAt = 0
let stopReconnect = null
function reloadIfStale() {
  if (document.visibilityState !== 'visible') return
  if (Date.now() - lastLoadedAt < 30000) return
  if (isDayView.value) loadEntries()
  else if (isPeriodView.value) loadPeriodData()
}

onMounted(() => {
  window.addEventListener('zoya:open-add', reminderAdd)
  window.addEventListener('zoya:meal-rated', reminderRated)
  guide.journey('j_diary')
  cacheClean()
  loadEntries()
  // Приложение стартовало заново — возможно, посреди ввода
  setTimeout(offerDraft, 800)
  document.addEventListener('visibilitychange', reloadIfStale)
  stopReconnect = onReconnect(reloadIfStale)
  nowTimer = setInterval(() => {
    now.value = new Date()
    // Проверяем смену дня (полночь)
    const newDateStr = toDateStr(new Date())
    if (newDateStr !== currentDateStr) {
      currentDateStr = newDateStr
      today.value = new Date()
      // Перезагружаем данные если на дневном табе
      if (isDayView.value) loadEntries()
    }
  }, 60000)
})
onUnmounted(() => {
  window.removeEventListener('zoya:open-add', reminderAdd)
  window.removeEventListener('zoya:meal-rated', reminderRated)
  document.body.style.overflow = ''
  document.removeEventListener('visibilitychange', reloadIfStale)
  if (stopReconnect) { stopReconnect(); stopReconnect = null }
  clearInterval(nowTimer)
  if (dailyPollTimer) { clearInterval(dailyPollTimer); dailyPollTimer = null }
  stopFeedbackPolling()
})

// keep-alive: при возврате на вкладку обновляем данные и перезапускаем таймеры
onActivated(() => {
  diaryHidden.value = false
  const newDateStr = toDateStr(new Date())
  if (newDateStr !== currentDateStr) {
    currentDateStr = newDateStr
    today.value = new Date()
  }
  now.value = new Date()
  if (isDayView.value) loadEntries()
  else if (isPeriodView.value) loadPeriodData()
  if (!nowTimer) {
    nowTimer = setInterval(() => {
      now.value = new Date()
      const nd = toDateStr(new Date())
      if (nd !== currentDateStr) {
        currentDateStr = nd
        today.value = new Date()
        if (isDayView.value) loadEntries()
      }
    }, 60000)
  }
})

onDeactivated(() => {
  diaryHidden.value = true
  if (nowTimer) { clearInterval(nowTimer); nowTimer = null }
  if (dailyPollTimer) { clearInterval(dailyPollTimer); dailyPollTimer = null }
  stopFeedbackPolling()
})

// ── Данные периодов (неделя/месяц) ──────────────────────────────────────────
const statusRevision = ref(0)
async function onCompletenessChanged({ date }) {
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i)
    if (key?.startsWith('zc:summary:') || key?.startsWith('zc:calendar:')) localStorage.removeItem(key)
  }
  reportCardCache.at = 0; reportCardCache.resp = null
  statusRevision.value++
  for (const key of Object.keys(stripMonths)) delete stripMonths[key]
  const [year, month] = date.split('-').map(Number)
  await loadCalendarMonth(year, month - 1)
  loadStripMonth(year, month - 1)
  if (isPeriodView.value) await loadPeriodData({ keepDay: true })
}
const periodData = ref(null)
const periodLoading = ref(false)

// Попап конкретного дня
const selectedPeriodDate = ref(null)

function onTabClick(key) {
  if (key === 'week') { guide.markWeekSeen(); guide.journey('j_week') }
  if (key === activeTab.value) {
    selectedPeriodDate.value = null
  } else {
    activeTab.value = key
  }
}
const periodDayEntries = ref([])
const periodDayGroupMeta = ref({})
const periodDayLoading = ref(false)
const periodDayPrevEaten = ref(null)
const periodDayFeedback = ref('')

// Навигация по месяцам
const monthViewStart = ref(null)
const monthViewEnd = ref(null)

// Диапазон вкладки «аналитика» (решение владельца 27.10.2026): по умолчанию —
// последняя серия дней с записями подряд (defaultRange в utils/periodCharts.js),
// меньше двух дней с записями — пример на тестовых данных (utils/periodDemo.js).
// Период меняется селектором: меню с пресетами применяет сразу, «Свой период»
// — два нажатия на календаре, второе применяет. В своём периоде можно
// включить сегодня; готовые диапазоны по умолчанию заканчиваются вчера.
const trackedDays = ref([])
const trackedLoaded = ref(false)
const rangeFrom = ref(null)          // применённый период
const rangeTo = ref(null)
const rangeKey = ref(null)           // пункт меню, которому равен период: streak | 14 | 30 | all | custom
const menuOpen = ref(false)
const pickMode = ref('none')         // 'none' | 'start' | 'end'
const pickStart = ref(null)          // выбранное начало, пока ждём конец
const isDemo = ref(false)
// После выбора дат календарь показывает месяц конца периода
const calendarFocus = ref(null)
const yesterdayStr = computed(() => toDateStr(yesterday.value))
// Календарь месяца красится по своим данным, независимо от выбранного периода
const calendarData = ref(null)
const diaryFirstDay = ref(null)
// Пункты меню периода. Подписи с датами — только у серии и своего периода
const periodMenu = computed(() => {
  const y = yesterdayStr.value
  const streak = lastStreak(trackedDays.value, y)
  const items = []
  if (streak && streak.from !== streak.to) {
    const n = daysBetween(streak.from, streak.to) + 1
    items.push({ key: 'streak', label: 'Последняя серия', caption: `${rangeLabel(streak.from, streak.to)} · ${n} ${dayWordOf(n)}`, from: streak.from, to: streak.to })
  }
  items.push({ key: '14', label: '2 недели', from: addDaysYmd(y, -13), to: y })
  items.push({ key: '30', label: '30 дней', from: addDaysYmd(y, -29), to: y })
  items.push({ key: 'all', label: 'Весь дневник', caption: diaryFirstDay.value ? `с ${rangeLabel(diaryFirstDay.value, diaryFirstDay.value)}` : null, from: diaryFirstDay.value || addDaysYmd(y, -29), to: y })
  items.push({ key: 'custom', label: 'Свой период', caption: rangeKey.value === 'custom' && rangeFrom.value ? rangeLabel(rangeFrom.value, rangeTo.value) : null })
  return items.map(it => ({ ...it, on: rangeKey.value === it.key }))
})
// Какому пункту меню равен период; ничему — «свой»
function keyOfRange(from, to) {
  const hit = periodMenu.value.find(it => it.key !== 'custom' && it.from === from && it.to === to)
  return hit ? hit.key : 'custom'
}
// Календарь показывает записи и полный/неполный статус, включая сегодняшний день.
const calendarDaysNoToday = computed(() => calendarData.value?.days_with_entries || [])
const calendarKcalsNoToday = computed(() => calendarData.value?.day_kcals || {})
// Длина выбранного периода в днях
const periodDaysCount = computed(() => periodData.value?.period?.from && periodData.value?.period?.to
  ? daysBetween(periodData.value.period.from, periodData.value.period.to) + 1 : 0)
const weightPoints = ref([])
const cardOpen = reactive({ kcal: false, protein: false, fat: false, carbs: false, sat: false, sugar: false, fiber: false, water: false })

function rangeParams() {
  return rangeFrom.value && rangeTo.value ? { start: rangeFrom.value, end: rangeTo.value } : {}
}
// Все дни с записями — лёгкий запрос ради диапазона по умолчанию
async function loadTrackedDays() {
  try {
    const { data } = await api.get('/diary/tracked-days', { params: { tz } })
    trackedDays.value = data.days || []
    diaryFirstDay.value = data.first_day || null
  } catch { trackedDays.value = [] }
  trackedLoaded.value = true
}
function showDemo() {
  const n = NORMS.value
  const { periodData: pd, weightPoints: wp } = demoPeriod(
    { kcal: n.kcal.upper, protein: [n.protein.lower, n.protein.upper], fat: [n.fat.lower, n.fat.upper], carbs: [n.carbs.lower, n.carbs.upper] },
    yesterdayStr.value)
  isDemo.value = true
  periodData.value = pd
  weightPoints.value = wp
  rangeFrom.value = pd.period.from; rangeTo.value = pd.period.to
  calendarFocus.value = pd.period.to.slice(0, 7)
  rangeKey.value = 'custom'
  monthViewStart.value = null; monthViewEnd.value = null
  periodZoyaFeedback.value = ''
  periodLoading.value = false
  if (!calendarData.value) loadFocusedCalendar()
}
// Пункт меню: пресет применяется сразу, «Свой период» включает выбор на календаре
function pickMenu(it) {
  menuOpen.value = false
  guide.done('an_range')
  if (it.key === 'custom') { startPick(); return }
  rangeKey.value = it.key
  applyRange({ from: it.from, to: it.to }, it.key)
  scheduleColorHint()
}
// Свой период: подсказки Зои «Выбери дату начала» → «И дату окончания» над
// календарём (sticky, каждый раз заново), второе нажатие применяет сразу
// Над сеткой: хвостик вниз, первый ряд чисел остаётся открытым.
const PICK_HINT = { anchor: '[data-guide="cal-grid"]', place: 'above', again: true }
function startPick() {
  guide.hide()
  pickStart.value = null
  pickMode.value = 'start'
  guide.request('an_pick_start', PICK_HINT)
}
function onPick(date) {
  if (pickMode.value === 'start') {
    pickStart.value = date
    pickMode.value = 'end'
    guide.hide('an_pick_start')
    guide.request('an_pick_end', PICK_HINT)
    return
  }
  if (pickMode.value !== 'end') return
  // Нажал раньше начала — это новое начало, конец ждём дальше
  if (!pickStart.value || date < pickStart.value) { pickStart.value = date; return }
  const from = pickStart.value
  pickMode.value = 'none'; pickStart.value = null
  guide.hide('an_pick_end')
  applyRange({ from, to: date })
  scheduleColorHint()
}
function cancelPick() {
  if (pickMode.value === 'none') return
  pickMode.value = 'none'; pickStart.value = null
  guide.hide('an_pick_start'); guide.hide('an_pick_end')
}
function applyRange({ from, to }, key = null) {
  if (!from || !to) return
  cancelPick()
  rangeFrom.value = from; rangeTo.value = to
  rangeKey.value = key || keyOfRange(from, to)
  calendarFocus.value = to.slice(0, 7)
  loadPeriodData()
}

// Сводка месяца (дни с записями, калории по дням, планы) с кэшем на час;
// onFresh зовётся и с кэшем, и со свежим ответом
async function fetchMonthSummary(year, month, onFresh) {
  const pad = (n) => String(n).padStart(2, '0')
  const last = new Date(year, month + 1, 0)
  const start = `${year}-${pad(month + 1)}-01`
  const end = `${year}-${pad(month + 1)}-${pad(last.getDate())}`
  const ck = `calendar:${start}v2`
  const cached = cacheGet(ck)
  if (cached) onFresh(cached)
  try {
    const { data } = await api.get('/diary/summary', { params: { period: 'month', tz, start, end, ahead: end } })
    cacheSet(ck, data, 3600 * 1000)
    onFresh(data)
  } catch { /* останется кэш или пусто */ }
}
let calendarRequest = 0
async function loadCalendarMonth(year, month) {
  const request = ++calendarRequest
  await fetchMonthSummary(year, month, (data) => {
    if (request === calendarRequest) calendarData.value = data
  })
}
function loadFocusedCalendar() {
  const month = calendarFocus.value || rangeTo.value?.slice(0, 7) || toDateStr(today.value).slice(0, 7)
  const [year, number] = month.split('-').map(Number)
  return loadCalendarMonth(year, number - 1)
}

// ── Лента дней вкладки «неделя»: цвета дней по месяцам, догружаются при прокрутке ──
const stripMonths = reactive({})      // 'YYYY-MM' → сводка месяца
const stripLoading = new Set()
const weekStripRef = ref(null)
const weekStripH = ref(92)
let weekStripRo = null
watch(weekStripRef, (c) => {
  weekStripRo?.disconnect(); weekStripRo = null
  const el = c?.rootEl || c?.$el
  if (!(el instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return
  const upd = () => { weekStripH.value = Math.round(el.offsetHeight) + 4 }
  upd()
  weekStripRo = new ResizeObserver(upd); weekStripRo.observe(el)
}, { flush: 'post' })
function loadStripMonth(year, month) {
  const key = `${year}-${String(month + 1).padStart(2, '0')}`
  if (stripMonths[key] || stripLoading.has(key)) return
  stripLoading.add(key)
  fetchMonthSummary(year, month, (data) => { stripMonths[key] = data }).finally(() => stripLoading.delete(key))
}
function onStripReach(date) {
  const y = Number(date.slice(0, 4)), m = Number(date.slice(5, 7)) - 1
  loadStripMonth(y, m)
  // и месяц перед ним, чтобы следующий свайп не ждал ответа
  loadStripMonth(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1)
}
const stripDays = computed(() => {
  const t = toDateStr(today.value)
  const set = new Set(periodData.value?.days_with_entries || [])
  for (const d of Object.values(stripMonths)) for (const x of d.days_with_entries || []) set.add(x)
  return [...set]
})
const stripDayStates = computed(() => {
  const out = {}
  for (const data of Object.values(stripMonths)) Object.assign(out, data.day_states || {})
  Object.assign(out, calendarData.value?.day_states || {}, periodData.value?.day_states || {})
  return out
})
const stripKcals = computed(() => {
  const out = {}
  for (const d of Object.values(stripMonths)) Object.assign(out, d.day_kcals || {})
  Object.assign(out, periodData.value?.day_kcals || {})
  return out
})

async function loadWeightRange(from, to) {
  try {
    const { data } = await api.get('/weight/history', { params: { from, to } })
    weightPoints.value = (data || []).map(r => ({ day: r.day || String(r.logged_at).slice(0, 10), kg: Number(r.weight_kg) })).filter(p => Number.isFinite(p.kg))
  } catch { weightPoints.value = [] }
}

async function loadPeriodData({ keepDay = false } = {}) {
  if (!isPeriodView.value || !authStore.user) return
  if (!keepDay) selectedPeriodDate.value = null
  const period = isMonthView.value ? 'range' : activeTab.value
  if (period === 'range') {
    if (!trackedLoaded.value) await loadTrackedDays()
    if (!rangeFrom.value) {
      const def = defaultRange(trackedDays.value, yesterdayStr.value)
      if (!def) { showDemo(); return }
      rangeFrom.value = def.from; rangeTo.value = def.to
      rangeKey.value = keyOfRange(def.from, def.to)
      calendarFocus.value = def.to.slice(0, 7)
    }
  }
  const params = { period, tz }
  if (showFiber.value) params.fiber = 1
  if (showProtein.value) params.protein = 1
  if (showKcal.value) params.kcal = 1
  if (showFat.value) params.fats = 1
  if (showCarbs.value) params.sugars = 1
  if (showCarbs.value) params.carbs = 1
  if (showWater.value) params.water = 1
  if (period === 'range') {
    Object.assign(params, rangeParams())
    if (params.end === toDateStr(today.value)) params.include_today = 1
  }
  // Запланированные дни впереди: неделя смотрит на 7 дней
  if (period === 'week') {
    const d = new Date(today.value); d.setDate(d.getDate() + 7)
    params.ahead = toDateStr(d)
  }
  const ck = `summary:${period}:${params.start || ''}:${params.end || ''}:${params.ahead || ''}:${params.fiber || 0}:${params.protein || 0}:${params.kcal || 0}:${params.fats || 0}:${params.carbs || 0}:${params.water || 0}:${params.sugars || 0}:${params.include_today || 0}v9`

  // Stale-while-revalidate: show cached data immediately
  const cached = cacheGet(ck)
  if (cached) {
    periodData.value = cached
    periodLoading.value = false
  } else {
    periodLoading.value = true
  }
  if (period === 'range' && !calendarData.value) loadFocusedCalendar()
  if (period === 'week') {
    // Лента дней: первый день дневника и два последних месяца для цветов
    if (!trackedLoaded.value) loadTrackedDays()
    const y = today.value.getFullYear(), m = today.value.getMonth()
    loadStripMonth(y, m)
    loadStripMonth(m === 0 ? y - 1 : y, m === 0 ? 11 : m - 1)
  }

  try {
    const { data } = await api.get('/diary/summary', { params })
    periodData.value = data
    cacheSet(ck, data, 3600 * 1000)
    if (period === 'range') {
      // Аналитика Зои за период идёт по тем же датам (как у календарного месяца)
      monthViewStart.value = data.period?.from || null
      monthViewEnd.value = data.period?.to || null
      isDemo.value = false
      if (data.first_day) diaryFirstDay.value = data.first_day
      if (data.period?.from && showWeight.value) loadWeightRange(data.period.from, data.period.to)
    }
    loadSavedPeriodFeedback()
  } catch {
    if (!cached) periodData.value = null
  } finally {
    periodLoading.value = false
  }
}

// Листание календаря меняет только его месяц; выбранный период не трогает
function onMonthChange(year, month) {
  loadCalendarMonth(year, month)
}

// ── Ряды и подписи карточек периода ──
const rDays = computed(() => periodData.value?.days || [])
const rFrom = computed(() => periodData.value?.period?.from || null)
const rTo = computed(() => periodData.value?.period?.to || null)
// Столбик — всегда день (решение владельца 26.10.2026): на долгих сроках график
// прокручивается по горизонтали (DayBars), а не укрупняется в недели
const rScale = computed(() => 'day')
const seriesOf = (key, rows) => rFrom.value && rTo.value ? bucketSeries(rows || rDays.value, key, rFrom.value, rTo.value, rScale.value) : []
const kcalBuckets = computed(() => seriesOf('kcal'))
const proteinBuckets = computed(() => seriesOf('protein'))
const fatBuckets = computed(() => seriesOf('fat'))
const carbsBuckets = computed(() => seriesOf('carbs'))
const fiberBuckets = computed(() => seriesOf('fiber'))
const waterBuckets = computed(() => seriesOf('water'))
const satRows = computed(() => Object.entries(periodData.value?.fats_breakdown?.by_day || {}).map(([day, v]) => ({ day, sat: v.sat, complete: periodData.value?.day_states?.[day]?.complete })).filter(r => r.sat != null))
const satBuckets = computed(() => seriesOf('sat', satRows.value))
const sugarRows = computed(() => Object.entries(periodData.value?.sugars_breakdown?.by_day || {}).map(([day, v]) => ({ day, free: v.free, complete: periodData.value?.day_states?.[day]?.complete })).filter(r => r.free != null))
const sugarBuckets = computed(() => seriesOf('free', sugarRows.value))
const okOf = (rows, key, norm, word = 'в норме') => {
  const r = daysInNorm(rows, key, norm)
  if (!r.total) return { text: '', good: true }
  return { text: `${r.ok} ${dayWordOf(r.ok)} из ${r.total} ${word}`, good: r.ok / r.total >= 0.5 }
}
const kcalOk = computed(() => okOf(rDays.value, 'kcal', { upper: NORMS.value.kcal.upper }, 'не выше нормы'))
// Подпись нормы калорий: с дефицитом из профиля, если он задан (владелец 27.10.2026)
const kcalNormLines = computed(() => {
  const lines = [{ text: `${NORMS.value.kcal.upper} ккал,`, detail: 'норма' }]
  const pct = effectiveDeficitPct.value
  if (pct < 0 && NORMS.value.kcal.lower) lines.push({ text: `${NORMS.value.kcal.lower} ккал,`, detail: `дефицит ${Math.abs(pct)}%`, dashed: true })
  return lines
})
const kcalExtraLines = computed(() => NORMS.value.kcal.lower && NORMS.value.kcal.lowerLabel
  ? [{ v: NORMS.value.kcal.lower, t: NORMS.value.kcal.lowerLabel, below: true }] : [])
const fiberOk = computed(() => okOf(rDays.value, 'fiber', { lower: NORMS.value.fiber.lower, upper: NORMS.value.fiber.upper }))
const satOk = computed(() => okOf(satRows.value, 'sat', { upper: satLimit.value }, 'в пределах'))
const sugarOk = computed(() => okOf(sugarRows.value, 'free', { upper: sugarLimit.value }, 'в пределах'))

// Вес: тенденция за период, изменение и скорость; дефицит для ожидаемого пути
const weightTrendPts = computed(() => rFrom.value && rTo.value ? weightTrend(weightPoints.value, rFrom.value, rTo.value) : [])
const weightValue = computed(() => {
  const t = weightTrendPts.value
  if (t.length) return t[t.length - 1].kg
  const vis = weightPoints.value.filter(p => p.day >= rFrom.value && p.day <= rTo.value)
  return vis.length ? vis[vis.length - 1].kg : null
})
// Тенденция «свежая», если её последняя точка не старше двух недель к концу
// периода: иначе скорость считалась бы по давним замерам и вводила в заблуждение
const weightTrendFresh = computed(() => {
  const t = weightTrendPts.value
  if (!t.length || !rTo.value) return false
  return (new Date(rTo.value) - new Date(t[t.length - 1].day)) / 86400000 <= 14
})
const weightSub = computed(() => {
  const t = weightTrendPts.value
  if (!t.length) return 'последний замер'
  return weightTrendFresh.value ? 'по тенденции' : `по тенденции на ${shortDateOf(t[t.length - 1].day)}`
})
const weightNormText = computed(() => {
  const t = weightTrendPts.value
  if (t.length < 2) return ''
  const delta = Math.round((t[t.length - 1].kg - t[0].kg) * 10) / 10
  const sign = delta < 0 ? '−' : delta > 0 ? '+' : '±'
  let s = `${sign}${Math.abs(delta).toFixed(1)} кг за период`
  const rate = weightTrendFresh.value && t.length >= 14 ? weeklyRate(t) : null
  if (rate != null) s += ` · ${rate < 0 ? '−' : '+'}${Math.abs(rate).toFixed(1)} в нед`
  return s
})
const weightDeficitKcal = computed(() => {
  const pct = effectiveDeficitPct.value
  return pct < 0 && NORMS.value.kcal.upper ? Math.round(NORMS.value.kcal.upper * (-pct) / 100) : 0
})

function onBarOpenDay(date) { guide.done('an_bar'); selectPeriodDay(date) }
async function selectPeriodDay(date) {
  if (selectedPeriodDate.value === date) {
    selectedPeriodDate.value = null
    return
  }
  selectedPeriodDate.value = date
  periodDayLoading.value = true
  periodDayFeedback.value = ''
  const ck = `diary:${date}`
  try {
    const { data } = await api.get('/diary', { params: { date, tz } })
    periodDayEntries.value = data.entries || []
    periodDayGroupMeta.value = indexGroups(data.groups)
    periodDayPrevEaten.value = data.prev_last_eaten_at || null
    cacheSet(ck, data, 24 * 3600 * 1000)
  } catch {
    const cached = cacheGet(ck)
    if (cached) {
      periodDayEntries.value = cached.entries || []
      periodDayGroupMeta.value = indexGroups(cached.groups)
      periodDayPrevEaten.value = cached.prev_last_eaten_at || null
    } else {
      periodDayEntries.value = []
      periodDayGroupMeta.value = {}
    }
  } finally {
    periodDayLoading.value = false
  }
  // Загрузить сохранённый фидбэк Зои за этот день (у будущего дня его нет)
  if (date > toDateStr(today.value)) return
  try {
    const { data } = await api.get('/ai/zoya-feedback', { params: { date } })
    if (data.feedback) periodDayFeedback.value = data.feedback
  } catch { /* silent */ }
}

// Блокировка скролла фона при открытом попапе
watch(selectedPeriodDate, (val) => {
  document.body.style.overflow = val ? 'hidden' : ''
  resetPeriodDayDetails()
})

// Computed для попапа дня
const periodDayTotals = computed(() => {
  const sum = (field) => Math.round(periodDayEntries.value.reduce((acc, e) => acc + (parseFloat(e[field]) || 0), 0) * 10) / 10
  const vitamins = {}
  const noVitSet = new Set()
  for (const entry of periodDayEntries.value) {
    if (!entry.vitamins) continue
    for (const [key, val] of Object.entries(entry.vitamins)) {
      if (key === '_no_vitamins') {
        if (Array.isArray(val)) val.forEach(n => noVitSet.add(n))
        continue
      }
      vitamins[key] = (vitamins[key] ?? 0) + val
    }
  }
  for (const key of Object.keys(vitamins)) {
    vitamins[key] = Math.round(vitamins[key] * 100) / 100
  }
  vitamins._no_vitamins = [...noVitSet]
  return { kcal: sum('kcal'), protein: sum('protein'), fat: sum('fat'), carbs: sum('carbs'), fiber: sum('fiber'), water: sum('water_ml'), vitamins }
})

const periodDayMealGroups = computed(() =>
  buildMealGroups(periodDayEntries.value, periodDayGroupMeta.value)
)

const periodDayFirstGap = computed(() => {
  if (!periodDayPrevEaten.value || !periodDayMealGroups.value.length) return null
  const prevTime = new Date(periodDayPrevEaten.value)
  const oldest = periodDayMealGroups.value.at(-1)
  const firstTime = new Date(oldest.entries[0].eaten_at)
  const prevEnd = new Date(prevTime.getTime() + 20 * 60000)
  const gapMin = (firstTime - prevEnd) / 60000
  if (gapMin > 48 * 60) return { noText: true }
  return { label: formatGap(gapMin) }
})

function periodDayGapLabel(prevGroup, nextGroup) {
  const endTime = groupEndTime(prevGroup)
  const startTime = new Date(nextGroup.entries[0].eaten_at)
  const gapMin = Math.round((startTime - endTime) / 60000)
  return formatGap(gapMin)
}

// Форматирование даты для заголовка попапа: "3 марта"
const MONTHS_FULL = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const MONTHS_NOM = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

function dayWord(n) {
  const a = n % 10, b = n % 100
  if (b >= 11 && b <= 14) return 'дней'
  if (a === 1) return 'день'
  if (a >= 2 && a <= 4) return 'дня'
  return 'дней'
}

const monthSummaryTitle = computed(() => {
  if (isWeekView.value) return 'В среднем за неделю'
  if (monthViewStart.value) {
    const m = parseInt(monthViewStart.value.slice(5, 7), 10) - 1
    return `В среднем за ${MONTHS_NOM[m]}`
  }
  return 'В среднем за последние 30 дней'
})
const isPeriodDayFuture = computed(() => !!selectedPeriodDate.value && selectedPeriodDate.value > toDateStr(today.value))
const periodDayTitle = computed(() => {
  if (!selectedPeriodDate.value) return ''
  const d = new Date(selectedPeriodDate.value + 'T12:00:00')
  // Будущий день — это план, а не итог
  return `${isPeriodDayFuture.value ? 'План на' : 'Всего за'} ${d.getDate()} ${MONTHS_FULL[d.getMonth()]}`
})

// Зоя аналитика за период. У вкладки «период» сервер получает period
// 'month' с датами диапазона: кэш и отпечаток дневника считаются по ним.
const feedbackPeriod = computed(() => isMonthView.value ? 'month' : activeTab.value)
const periodZoyaFeedback = ref('')
const periodZoyaLoading = ref(false)
const periodZoyaError = ref('')
const periodRedoMode = ref(false)
// Менялся ли дневник с момента разбора — считает сервер по отпечатку
// содержимого (lib/meals.js, diaryFingerprint). Пока сервер не ответил,
// считаем, что менялся: лучше лишнее обновление, чем запертая кнопка.
const zoyaStale = ref(true)
const periodZoyaStale = ref(true)
// diaryChanged ловит правку прямо сейчас, не дожидаясь ответа сервера:
// добавил запись — кнопка оживает сразу, без перезагрузки экрана.
const canRedoDayFeedback = computed(() => zoyaStale.value || diaryChanged.value)
let feedbackAbort = null  // AbortController для отмены стухших запросов

// Секции недельного анализа
const WEEK_SECTIONS = [
  { id: 'general',  label: 'Общий анализ: баланс КБЖУ, метод тарелки', default: true },
  { id: 'water',    label: 'Водный баланс, калий-натрий и отёки',   default: false },
  { id: 'gi',       label: 'Гликемический индекс (ГИ) и ГН',       default: false },
  { id: 'sugars',   label: 'Скрытые сахара',                       default: false },
  { id: 'fats',     label: 'Насыщенные жиры',                      default: false },
  { id: 'protein',  label: 'Больше о белке',                       default: false },
  { id: 'fodmap',   label: 'FODMAP',                               default: false },
]
const selectedSections = ref(WEEK_SECTIONS.filter(s => s.default).map(s => s.id))

let feedbackPollTimer = null

function stopFeedbackPolling() {
  if (feedbackAbort) { feedbackAbort.abort(); feedbackAbort = null }
  if (feedbackPollTimer) { clearInterval(feedbackPollTimer); feedbackPollTimer = null }
}

async function loadPeriodZoyaFeedback() {
  if (!authStore.hasAiAccess) { authStore.showPaywall = true; return }
  stopFeedbackPolling()
  periodZoyaLoading.value = true
  periodZoyaError.value = ''
  try {
    const period = feedbackPeriod.value
    const todayStr = toDateStr(today.value)
    const payload = { tz, date: todayStr, period }
    if (period === 'month' && monthViewStart.value && monthViewEnd.value) {
      payload.start = monthViewStart.value
      payload.end = monthViewEnd.value
      payload.date = monthViewStart.value
    }
    if (period === 'week' && selectedSections.value.length > 0) {
      payload.sections = selectedSections.value
    }
    const { data } = await api.post('/ai/zoya-feedback', payload)
    if (data.feedback) {
      // Быстрый ответ (из кэша или без секций)
      periodZoyaFeedback.value = data.feedback
      periodZoyaLoading.value = false
      return
    }
    // Бэкенд генерирует в фоне — запускаем поллинг
    startFeedbackPolling()
  } catch (err) {
    periodZoyaError.value = err.response?.data?.error || 'Не удалось получить анализ'
    periodZoyaLoading.value = false
  }
}

function startFeedbackPolling() {
  const ac = new AbortController()
  feedbackAbort = ac
  let attempts = 0
  const maxAttempts = 40  // 40 × 5s = ~3.3 мин

  feedbackPollTimer = setInterval(() => {
    if (ac.signal.aborted) { stopFeedbackPolling(); return }
    attempts++
    ;(async () => {
      try {
        const period = feedbackPeriod.value
        const dateKey = (period === 'month' && monthViewStart.value)
          ? `${period}-${monthViewStart.value}`
          : `${period}-${toDateStr(today.value)}`
        const params = { date: dateKey, tz, period }
        if (period === 'month' && monthViewStart.value && monthViewEnd.value) {
          params.start = monthViewStart.value
          params.end = monthViewEnd.value
        }
        const { data } = await api.get('/ai/zoya-feedback', { params })
        if (data.feedback) {
          periodZoyaFeedback.value = data.feedback
          periodZoyaStale.value = data.stale !== false
          periodZoyaLoading.value = false
          stopFeedbackPolling()
          return
        }
        if (data.status !== 'generating' || attempts >= maxAttempts) {
          periodZoyaError.value = attempts >= maxAttempts
            ? 'Анализ занял слишком много времени. Попробуйте позже.'
            : 'Не удалось получить анализ'
          periodZoyaLoading.value = false
          stopFeedbackPolling()
        }
      } catch {
        // Ошибка сети — продолжаем поллить
        if (attempts >= maxAttempts) {
          periodZoyaError.value = 'Не удалось получить анализ'
          periodZoyaLoading.value = false
          stopFeedbackPolling()
        }
      }
    })().catch(() => {})
  }, 5000)
}

async function redoPeriodZoyaFeedback() {
  periodRedoMode.value = false
  periodZoyaFeedback.value = ''
  periodZoyaError.value = ''
  await loadPeriodZoyaFeedback()
}

// Загрузить сохранённый period feedback
async function loadSavedPeriodFeedback() {
  stopFeedbackPolling()
  periodZoyaFeedback.value = ''
  periodZoyaError.value = ''
  periodRedoMode.value = false
  if (!authStore.user) return
  try {
    const period = feedbackPeriod.value
    const dateKey = (period === 'month' && monthViewStart.value)
      ? `${period}-${monthViewStart.value}`
      : `${period}-${toDateStr(today.value)}`
    const params = { date: dateKey, tz, period }
    if (period === 'month' && monthViewStart.value && monthViewEnd.value) {
      params.start = monthViewStart.value
      params.end = monthViewEnd.value
    }
    const { data } = await api.get('/ai/zoya-feedback', { params })
    periodZoyaStale.value = data.stale !== false
    if (data.feedback) {
      periodZoyaFeedback.value = data.feedback
    } else if (data.status === 'generating') {
      // Генерация уже идёт (например, запустили и перезагрузили страницу)
      periodZoyaLoading.value = true
      startFeedbackPolling()
    }
  } catch { /* silent */ }
}

// ── Группировка приёмов пищи ─────────────────────────────────────────────────
// Источник истины — meal_group_id из БД (таблица zoya_meal_groups).
// Склейка по времени осталась только как фолбэк для записей, ещё не
// привязанных к группе (до прогона миграции add_meal_groups.sql).
function buildMealGroups(list, metaById = {}) {
  const sorted = [...list].sort(
    (a, b) => new Date(a.eaten_at) - new Date(b.eaten_at) || (a.id - b.id)
  )

  const groups = []
  const byGroupId = new Map()

  for (const entry of sorted) {
    const gid = entry.meal_group_id || null

    if (gid) {
      let g = byGroupId.get(gid)
      if (!g) {
        g = { id: gid, entries: [], meta: metaById[gid] || null }
        byGroupId.set(gid, g)
        groups.push(g)
      }
      g.entries.push(entry)
      continue
    }

    // Фолбэк: цепочка с разрывом не более 20 минут
    const last = groups.at(-1)
    const canChain = last && !last.id
    const diffMin = canChain
      ? (new Date(entry.eaten_at) - new Date(last.entries.at(-1).eaten_at)) / 60000
      : Infinity
    if (diffMin > 20) groups.push({ id: null, entries: [entry], meta: null })
    else last.entries.push(entry)
  }

  // Порядок групп — по времени первой записи, новые сверху
  groups.sort((a, b) => new Date(a.entries[0].eaten_at) - new Date(b.entries[0].eaten_at))
  return groups.reverse()
}

const mealGroups = computed(() => buildMealGroups(entries.value, mealGroupMeta.value))

// Подсказки Зои (services/guide.js) по состоянию сегодняшнего дня: пустой
// дневник — «+», есть блюдо — правка блюда, дальше вопрос Зое и неделя.
// Одна на экране, каждая один раз на аккаунт; сам сервис решает, показывать ли.
const addInitialTab = ref(null)
watch([loading, mealGroups, activeTab, () => guideState.loaded, () => guideState.off, () => guideActive.value, showAdd], () => {
  if (loading.value || activeTab.value !== 'today' || !guideState.loaded || showAdd.value || guideActive.value) return
  if (mealGroups.value.length === 0) {
    if (!guideState.has_entries) guide.request('first_add', { anchor: '[data-guide="fab"]', place: 'above' })
    return
  }
  if (guide.request('dish_edit', { anchor: '[data-guide="dish"]', place: 'below' })) return
  // Появились шкалы, а нормы средние — вместо жёлтого блока пузырь Зои
  if (!effectiveKcalNorm.value
      && guide.request('norms', { anchor: '[data-guide="kcal"]', place: 'below', onCta: () => router.push('/profile/body') })) return
  const ageMs = guideState.created_at ? Date.now() - new Date(guideState.created_at).getTime() : 0
  const dishes = new Set(mealGroups.value.flatMap(g => g.entries.map(e => e.dish_id || 'e' + e.id))).size
  if ((dishes >= 3 || ageMs > 20 * 3600e3) && !authStore.user?.goals_data
      && guide.request('ask_zoya', { anchor: '[data-guide="nav-zoya"]', place: 'above' })) return
  if (ageMs > 2 * 864e5) guide.request('week', { anchor: '[data-guide="tab-week"]', place: 'below' })
}, { flush: 'post' })

// Реплика про цвета дней стоит под нижним рядом квадратов и указывает на
// них: середина последней строки текущего месяца (владелец 28.10.2026)
function calendarLastRowAnchor() {
  const cells = [...document.querySelectorAll('[data-guide="cal-days"] [data-date]')]
  if (!cells.length) return null
  const bottom = Math.max(...cells.map(c => c.offsetTop))
  const row = cells.filter(c => c.offsetTop === bottom)
  return row[Math.floor((row.length - 1) / 2)] || cells[cells.length - 1]
}

// Explain calendar colors after a period was applied, never when its menu opens.
const colorHintReady = ref(false)
const colorHintWaiting = ref(false)
let colorHintTimer = null
function scheduleColorHint() {
  clearTimeout(colorHintTimer)
  colorHintReady.value = false
  colorHintWaiting.value = true
  colorHintTimer = setTimeout(() => {
    colorHintWaiting.value = false
    colorHintReady.value = true
  }, 1400)
}
watch(menuOpen, open => {
  if (!open) return
  clearTimeout(colorHintTimer)
  colorHintWaiting.value = false
  colorHintReady.value = false
  guide.hide('an_colors')
})
watch(isMonthView, visible => {
  if (visible) return
  clearTimeout(colorHintTimer)
  colorHintWaiting.value = false
  colorHintReady.value = false
  guide.hide('an_colors')
})
onUnmounted(() => clearTimeout(colorHintTimer))
// Trigger contextual analytics hints only when their target reaches the reading area.
const analyticsViewport = ref(0)
let analyticsScrollFrame = null
function onAnalyticsScroll() {
  if (!isMonthView.value || analyticsScrollFrame !== null) return
  analyticsScrollFrame = requestAnimationFrame(() => {
    analyticsScrollFrame = null
    analyticsViewport.value++
  })
}
function analyticsAnchorReady(anchor) {
  const el = typeof anchor === 'function' ? anchor() : document.querySelector(anchor)
  if (!el || !isMonthView.value || diaryHidden.value) return false
  const rect = el.getBoundingClientRect()
  const height = window.visualViewport?.height || window.innerHeight
  const top = window.visualViewport?.offsetTop || 0
  const focus = rect.top + Math.min(rect.height, 160) / 2
  return focus >= top + height * 0.2 && focus <= top + height * 0.65
}
function requestAnalyticsHint(key, anchor, place) {
  return guide.request(key, { anchor, place, ready: () => analyticsAnchorReady(anchor) })
}
onActivated(() => {
  window.addEventListener('scroll', onAnalyticsScroll, { capture: true, passive: true })
  window.addEventListener('resize', onAnalyticsScroll, { passive: true })
})
function stopAnalyticsScroll() {
  window.removeEventListener('scroll', onAnalyticsScroll, true)
  window.removeEventListener('resize', onAnalyticsScroll)
  if (analyticsScrollFrame !== null) cancelAnimationFrame(analyticsScrollFrame)
  analyticsScrollFrame = null
}
onDeactivated(stopAnalyticsScroll)
onUnmounted(stopAnalyticsScroll)
watch([periodData, periodLoading, () => guideState.loaded, () => guideActive.value, isMonthView, pickMode, menuOpen, colorHintReady, colorHintWaiting, analyticsViewport], () => {
  if (!isMonthView.value || !periodData.value?.sufficient || periodLoading.value || !guideState.loaded || guideActive.value || pickMode.value !== 'none' || menuOpen.value || colorHintWaiting.value) return
  if (requestAnalyticsHint('an_range', '[data-guide="an-range"]', 'below')) return
  if (colorHintReady.value && showKcalColors.value && !isDemo.value && requestAnalyticsHint('an_colors', calendarLastRowAnchor, 'below')) return
  if (periodDaysCount.value > 44 && requestAnalyticsHint('an_scroll', '[data-guide="an-chart"]', 'above')) return
  requestAnalyticsHint('an_bar', '[data-guide="an-chart"]', 'above')
}, { flush: 'post' })

// Строка карточки «Первые шаги»: ведём туда, где это делается
function onGuideGo(key) {
  if (key === 'photo') { openAdd(); return }
  if (key === 'repeat') { addInitialTab.value = 'recent'; openAdd(); return }
  if (key === 'edit') {
    if (mealGroups.value.length === 0) { openAdd(); return }
    mealGroupRefs.value[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    guide.request('dish_edit', { anchor: '[data-guide="dish"]', place: 'below', again: true })
    return
  }
  if (key === 'week') { onTabClick('week'); return }
  if (key === 'profile') router.push(authStore.user?.goals_data ? '/profile' : '/onboarding?phase=2')
}

// Конец приёма пищи: meal_end_at или fallback eaten_at + 20 мин
function groupEndTime(group) {
  const last = group.entries.at(-1)
  return last.meal_end_at
    ? new Date(last.meal_end_at)
    : new Date(new Date(last.eaten_at).getTime() + 20 * 60000)
}

// Время с последнего приёма пищи (только для "сегодня")
const lastMealGap = computed(() => {
  if (activeTab.value !== 'today' || !mealGroups.value.length) return null
  const newest = mealGroups.value[0]
  const endTime = groupEndTime(newest)
  const diffMin = (now.value - endTime) / 60000
  if (diffMin < 3) return null
  return formatGap(diffMin)
})

// Форматирование промежутка в минутах
function formatGap(rawMin) {
  const rounded = Math.round(Math.max(rawMin, 0) / 5) * 5
  if (rounded === 0) return '< 5 мин'
  if (rounded >= 1440) {
    const totalH = Math.floor(rounded / 60)
    const d = Math.floor(totalH / 24)
    const h = totalH % 24
    const m = rounded % 60
    let parts = []
    if (d > 0) parts.push(`${d} д`)
    if (h > 0) parts.push(`${h} ч`)
    if (m > 0) parts.push(`${m} мин`)
    return `~ ${parts.join(' ')}`
  }
  if (rounded >= 60) {
    const h = Math.floor(rounded / 60)
    const m = rounded % 60
    return m > 0 ? `~ ${h} ч ${m} мин` : `~ ${h} ч`
  }
  return `~ ${rounded} мин`
}

// Метка промежутка между приёмами
function gapLabel(prevGroup, nextGroup) {
  const endTime = groupEndTime(prevGroup)
  const startTime = new Date(nextGroup.entries[0].eaten_at)
  const gapMin = Math.round((startTime - endTime) / 60000)
  return formatGap(gapMin)
}

// Промежуток перед первым приёмом дня (от последнего приёма предыдущего дня)
// После реверса mealGroups: самый старый приём — последний в массиве
const firstGap = computed(() => {
  if (!prevLastEatenAt.value || !mealGroups.value.length) return null
  const prevTime = new Date(prevLastEatenAt.value)
  const oldest = mealGroups.value.at(-1)
  const firstTime = new Date(oldest.entries[0].eaten_at)
  // prev_last_eaten_at не имеет meal_end_at, используем fallback +20 мин
  const prevEnd = new Date(prevTime.getTime() + 20 * 60000)
  const gapMin = (firstTime - prevEnd) / 60000
  if (gapMin > 48 * 60) return { noText: true }
  return { label: formatGap(gapMin) }
})

// ── Итоги дня ─────────────────────────────────────────────────────────────────
function sumField(field) {
  return Math.round(entries.value.reduce((acc, e) => acc + (parseFloat(e[field]) || 0), 0) * 10) / 10
}

function aggregateVitamins() {
  const result = {}
  const noVitSet = new Set()
  for (const entry of entries.value) {
    if (!entry.vitamins) continue
    for (const [key, val] of Object.entries(entry.vitamins)) {
      if (key === '_no_vitamins') {
        if (Array.isArray(val)) val.forEach(n => noVitSet.add(n))
        continue
      }
      result[key] = (result[key] ?? 0) + val
    }
  }
  for (const key of Object.keys(result)) {
    result[key] = Math.round(result[key] * 100) / 100
  }
  result._no_vitamins = [...noVitSet]
  return result
}

const totals = computed(() => ({
  kcal:    sumField('kcal'),
  protein: sumField('protein'),
  fat:     sumField('fat'),
  carbs:   sumField('carbs'),
  fiber:   sumField('fiber'),
  water:   sumField('water_ml'),
  vitamins: aggregateVitamins(),
}))

// ── Фидбек от Зои ────────────────────────────────────────────────────────────

function formatZoyaFeedback(text) {
  const esc = s => s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
  const lines = text.split('\n')
  let html = ''
  let listType = null // 'ul' | 'ol' | null

  for (const raw of lines) {
    const line = raw.trim()

    // Bullet: "* text" or "- text"
    const bullet = line.match(/^[\*\-]\s+(.*)/)
    // Numbered: "1. text", "2) text"
    const numbered = line.match(/^(\d+)[\.\)]\s+(.*)/)
    // Header: "# text", "## text", "### text"
    const header = line.match(/^(#{1,3})\s+(.*)/)

    if (bullet || numbered) {
      const isBullet = !!bullet
      const tag = isBullet ? 'ul' : 'ol'
      const content = isBullet ? bullet[1] : numbered[2]
      if (listType !== tag) {
        if (listType) html += `</${listType}>`
        html += `<${tag} class="zf-list">`
        listType = tag
      }
      const valAttr = numbered ? ` value="${numbered[1]}"` : ''
      html += `<li${valAttr}>${fmtInline(esc(content))}</li>`
    } else {
      if (listType) { html += `</${listType}>`; listType = null }
      if (line === '') {
        if (html) html += '<div class="zf-spacer"></div>'
      } else if (header) {
        const level = header[1].length
        html += `<p class="zf-h${level}">${fmtInline(esc(header[2]))}</p>`
      } else {
        html += `<p>${fmtInline(esc(line))}</p>`
      }
    }
  }
  if (listType) html += `</${listType}>`
  return html
}

function fmtInline(text) {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

const zoyaFeedback  = ref('')
const zoyaLoading   = ref(false)
const zoyaError     = ref('')
const diaryChanged  = ref(false)
const feedbackSnapshot = ref('')  // entries snapshot when feedback was loaded

// Загрузить сохранённый feedback при смене дня
async function loadSavedFeedback() {
  zoyaFeedback.value = ''
  zoyaError.value = ''
  diaryChanged.value = false
  feedbackSnapshot.value = ''
  if (!authStore.user) return
  try {
    const { data } = await api.get('/ai/zoya-feedback', { params: { date: activeDateStr.value, tz } })
    zoyaStale.value = data.stale !== false
    if (data.feedback) {
      zoyaFeedback.value = data.feedback
      feedbackSnapshot.value = buildEntriesHash()
    }
  } catch { /* silent */ }
}

function buildEntriesHash() {
  return entries.value
    .map(e => `${e.id}:${e.product_name}:${e.weight_g}:${e.kcal}`)
    .sort()
    .join('|')
}

// Отслеживаем изменения в дневнике после загрузки feedback
const currentEntriesHash = computed(() => buildEntriesHash())
watch(currentEntriesHash, (newHash) => {
  if (zoyaFeedback.value && feedbackSnapshot.value) {
    diaryChanged.value = newHash !== feedbackSnapshot.value
  }
})

let dailyPollTimer = null

async function loadZoyaFeedback() {
  if (!authStore.hasAiAccess) { authStore.showPaywall = true; return }
  zoyaLoading.value = true
  zoyaError.value = ''
  zoyaFeedback.value = ''
  if (dailyPollTimer) { clearInterval(dailyPollTimer); dailyPollTimer = null }
  try {
    const dateStr = activeDateStr.value
    const { data } = await api.post('/ai/zoya-feedback', { tz, date: dateStr })
    if (data.feedback) {
      zoyaFeedback.value = data.feedback
      feedbackSnapshot.value = buildEntriesHash()
      diaryChanged.value = false
      zoyaStale.value = false
      zoyaLoading.value = false
      return
    }
    // Бэкенд генерирует в фоне — поллим GET
    let attempts = 0
    const maxAttempts = 30 // 30 × 3s = ~1.5 мин
    dailyPollTimer = setInterval(() => {
      attempts++
      ;(async () => {
        try {
          const { data: pollData } = await api.get('/ai/zoya-feedback', { params: { date: dateStr, tz } })
          if (pollData.feedback) {
            zoyaFeedback.value = pollData.feedback
            feedbackSnapshot.value = buildEntriesHash()
            diaryChanged.value = false
            zoyaLoading.value = false
            clearInterval(dailyPollTimer); dailyPollTimer = null
            return
          }
        } catch { /* продолжаем */ }
        if (attempts >= maxAttempts) {
          zoyaError.value = 'Анализ занял слишком много времени. Попробуйте позже.'
          zoyaLoading.value = false
          clearInterval(dailyPollTimer); dailyPollTimer = null
        }
      })().catch(() => {})
    }, 3000)
  } catch (err) {
    zoyaError.value = err.response?.data?.error || 'Не удалось получить анализ'
    zoyaLoading.value = false
  }
}

// ── Действия ──────────────────────────────────────────────────────────────────
function openAdd(eatenAt = null) {
  if (!authStore.user) { authStore.openAuthPopup(); return }
  guide.done('first_add')
  if (typeof eatenAt === 'string') {
    addEatenAt.value = eatenAt
  } else if (activeTab.value === 'yesterday') {
    addEatenAt.value = computeYesterdayEatenAt()
  } else {
    addEatenAt.value = null
  }
  showAdd.value = true
}

function reminderAdd() { if (!diaryHidden.value) openAdd() }
function reminderRated(event) { onGroupUpdated(event.detail || {}) }

function computeYesterdayEatenAt() {
  const yDate = yesterday.value
  const sorted = [...entries.value]
    .filter(e => e.eaten_at)
    .sort((a, b) => new Date(a.eaten_at) - new Date(b.eaten_at))

  if (sorted.length === 0) {
    // Нет записей за вчера → 12:00
    const dt = new Date(yDate)
    dt.setHours(12, 0, 0, 0)
    return dt.toISOString()
  }

  const lastTime = new Date(sorted[sorted.length - 1].eaten_at)
  const plusOneHour = new Date(lastTime.getTime() + 60 * 60 * 1000)

  // Ограничить 23:59 того же дня, чтобы не уехать на следующий
  const cap = new Date(yDate)
  cap.setHours(23, 59, 0, 0)

  const result = plusOneHour > cap ? cap : plusOneHour
  return result.toISOString()
}

function updateDiaryCache() {
  const ck = `diary:${activeDateStr.value}`
  const isToday = activeDateStr.value === toDateStr(today.value)
  cacheSet(ck, {
    entries: entries.value,
    groups: Object.values(mealGroupMeta.value),
    prev_last_eaten_at: prevLastEatenAt.value,
  }, isToday ? 10 * 60 * 1000 : 24 * 3600 * 1000)
}

// Запись, сохранённая на другой день (в окошке времени выбрано «Завтра» или
// иная дата), в открытый день не кладём: иначе итоги и раскладки «за сегодня»
// до перечитывания считали бы завтрашнюю еду. Снимок того дня в кэше уже
// неверен — сбрасываем, чтобы он не показал день без этой записи.
function entryDateStr(entry) {
  const d = new Date(entry?.eaten_at)
  return Number.isNaN(d.getTime()) ? null : toDateStr(d)
}
function acceptEntryForOpenDay(entry) {
  const ds = entryDateStr(entry)
  let placed = false
  if (!ds || ds === activeDateStr.value) { entries.value.push(entry); placed = true }
  else cacheDel(`diary:${ds}`)
  if (selectedPeriodDate.value && (!ds || ds === selectedPeriodDate.value)) periodDayEntries.value.push(entry)
  return placed
}

async function onAdded(entry) {
  window.dispatchEvent(new Event('zoya:entry-saved'))
  guide.load(authStore.user)  // прогресс «Первых шагов» и has_entries
  const placed = acceptEntryForOpenDay(entry)
  updateDiaryCache()
  if (!placed) return
  // У свежей записи клетчатки может не быть ни в одном источнике — сервер
  // помечает такую fiber_pending. Просим оценку сразу: крутилку в пилюле
  // включает именно этот запрос, и без него на карточке стоял бы ноль до
  // ближайшей перезагрузки дня.
  ensureFiberEstimates(activeDateStr.value)
  await nextTick()

  // Найти группу, содержащую добавленную запись, и проскроллить к ней
  const groupIdx = mealGroups.value.findIndex(g =>
    g.entries.some(e => e.id === entry.id)
  )
  if (groupIdx >= 0 && mealGroupRefs.value[groupIdx]) {
    mealGroupRefs.value[groupIdx].scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

// Заметка / голод / сытость приёма пищи — живут в zoya_meal_groups
function onGroupUpdated({ groupId, ...fields }) {
  if (!groupId) return
  const cur = mealGroupMeta.value[groupId] || { id: groupId }
  mealGroupMeta.value = { ...mealGroupMeta.value, [groupId]: { ...cur, ...fields } }
  const pCur = periodDayGroupMeta.value[groupId]
  if (pCur) {
    periodDayGroupMeta.value = { ...periodDayGroupMeta.value, [groupId]: { ...pCur, ...fields } }
  }
  updateDiaryCache()
}

function onEntryUpdated({ id, ...fields }) {
  const entry = entries.value.find(e => e.id === id)
  if (entry) Object.assign(entry, fields)
  const pEntry = periodDayEntries.value.find(e => e.id === id)
  if (pEntry) Object.assign(pEntry, fields)
  updateDiaryCache()
}

// ── Редактирование времени приёма ─────────────────────────────────────────────
const showTimePicker    = ref(false)
const timePickerData    = ref(null)
const timePickerMode = ref('edit')
const timePickerBusy = ref(false)
const timePickerError = ref('')
const copyMealDate = ref('')
let copyMealClientId = null

function closeTimePicker() { if (!timePickerBusy.value) showTimePicker.value = false }
function onCopyMeal() {
  if (timePickerBusy.value) return
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  copyMealDate.value = toDateStr(tomorrow)
  copyMealClientId = uuid()
  timePickerError.value = ''
  timePickerMode.value = 'copy'
}

function onEditTime(data) {
  timePickerMode.value = 'edit'
  timePickerError.value = ''
  timePickerData.value = data
  showTimePicker.value = true
}

// Попап показывает деривированный вид правила приёма: чекбокс горит, только
// пока у варианта остались будущие даты. Сырое правило остаётся в
// timePickerData.repeat — его якорь нужен planRuleChange для поиска копий.
const timePickerRepeatView = computed(() =>
  timePickerData.value ? deriveRuleView(timePickerData.value.repeat) : null
)

async function onTimePickerConfirm({ date, startHour, startMin, endHour, endMin, repeat }) {
  if (!timePickerData.value || timePickerBusy.value) return
  timePickerBusy.value = true
  timePickerError.value = ''

  const { entryIds, repeat: oldRepeat } = timePickerData.value
  const dateStr = date || activeDateStr.value
  const eatenAt = new Date(`${dateStr}T00:00:00`)
  eatenAt.setHours(startHour, startMin, 0, 0)
  const mealEndAt = new Date(`${dateStr}T00:00:00`)
  mealEndAt.setHours(endHour, endMin, 0, 0)

  try {
    if (timePickerMode.value === 'copy') {
      await api.post('/diary/duplicate', {
        entry_ids: entryIds,
        client_id: copyMealClientId,
        copy_to: { eaten_at: eatenAt.toISOString(), meal_end_at: mealEndAt.toISOString() },
      })
      cacheDel(`diary:${dateStr}`)
      showTimePicker.value = false
      window.dispatchEvent(new Event('zoya:entry-saved'))
      await loadEntries()
      // Open the destination immediately, including when it is already selected.
      selectedPeriodDate.value = null
      await selectPeriodDay(dateStr)
      return
    }
    await api.patch('/diary/group-time', {
      entry_ids: entryIds,
      eaten_at: eatenAt.toISOString(),
      meal_end_at: mealEndAt.toISOString(),
    })

    // Дублирование приёма: чекбоксы изменились — досоздать/убрать копии.
    // Сравнение — с деривированным видом (с ним же работал пользователь);
    // сырое сравнение у исчерпанного правила считало бы повторную галочку
    // «завтра» отсутствием изменений и молча ничего не делало.
    if (repeat && ruleChanged(deriveRuleView(oldRepeat), repeat)) {
      // Якорь нового правила — дата приёма (для прошлых приёмов — сегодня),
      // а не якорь прошлого раунда: «завтра» от старого якоря — уже
      // прошедший день, и именно на этом дублирование молча делало ничего
      const newRule = { ...repeat, anchor_date: effectiveAnchorDate(dateStr) }
      const { add_offsets, remove_ranges } = planRuleChange(oldRepeat, newRule, dateStr)
      await api.post('/diary/duplicate', {
        entry_ids: entryIds,
        rule: newRule,
        add_offsets,
        remove_ranges,
        // Копия «на сегодня» ложится на уже прошедшее время суток —
        // сервер пропустит её без границы сегодняшних суток клиента
        not_before: todayStartIso(),
      })
      cacheClearFutureDiary(todayDateStr())
    }

    showTimePicker.value = false
    await loadEntries()
  } catch (err) {
    timePickerError.value = err.response?.data?.error || 'Не удалось сохранить. Проверьте связь и попробуйте ещё раз.'
    console.error('Update group time error:', err)
  } finally {
    timePickerBusy.value = false
  }
}

function ruleChanged(oldRule, newRule) {
  return !!oldRule?.today !== !!newRule?.today
    || !!oldRule?.tomorrow !== !!newRule?.tomorrow
    || !!oldRule?.weekdays !== !!newRule?.weekdays
    || !!oldRule?.alldays !== !!newRule?.alldays
}

// ── Редактирование блюда ─────────────────────────────────────────────────────
const showEditDish     = ref(false)
const editDishItems    = ref([])
const editDishIcons    = ref([])   // иконки продуктов блюда без фото (MealGroup)
const editDishPhotoUrl = ref(null)
const editDishProductPhotoCustom = ref(false)
const editDishName     = ref(null)
const editDishId       = ref(null)
const editDishFavorite = ref(false)
const editDishEntryIds = ref([])
const editDishEatenAt  = ref(null)
const editDishIsEditedCooking = ref(false)
const editDishCookedWeightG = ref(null)
const editDishMealMeta = ref(null)
const editDishDuplicateRule = ref(null)

function mapEntriesToEditItems(entries) {
  return entries.map(e => ({
    name: e.product_name,
    type: 'direct',
    weight_g: parseFloat(e.weight_g) || 0,
    // Штуки и порции — как было введено (см. utils/units.js)
    qty: e.qty != null ? parseFloat(e.qty) : null,
    unit_name: e.unit_name || null,
    unit_g: e.unit_g != null ? parseFloat(e.unit_g) : null,
    product_id: e.product_id,
    db_name: e.db_product_name || e.product_name,
    matched: !!e.product_id,
    source: e.product_source || null,
    kcal:    parseFloat(e.kcal) || 0,
    protein: parseFloat(e.protein) || 0,
    fat:     parseFloat(e.fat) || 0,
    carbs:   parseFloat(e.carbs) || 0,
    fiber:   parseFloat(e.fiber) || null,
    water:   parseFloat(e.water_ml) || null,
    per100: {
      kcal:    e.weight_g ? Math.round(parseFloat(e.kcal) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
      protein: e.weight_g ? Math.round(parseFloat(e.protein) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
      fat:     e.weight_g ? Math.round(parseFloat(e.fat) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
      carbs:   e.weight_g ? Math.round(parseFloat(e.carbs) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
      fiber:   e.weight_g && e.fiber ? Math.round(parseFloat(e.fiber) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
      water:   e.weight_g && e.water_ml ? Math.round(parseFloat(e.water_ml) / parseFloat(e.weight_g) * 100 * 10) / 10 : null,
    },
  }))
}

async function onDishClick(dish) {
  guide.done('dish_edit')
  guide.journey('j_edit')
  editDishPhotoUrl.value = dish.photo_url
  editDishIcons.value = dish.icons || []
  editDishProductPhotoCustom.value = !!dish.product_photo_custom
  editDishName.value = dish.name || dish.entries[0]?.product_name || null
  editDishId.value = dish.dish_id
  editDishFavorite.value = dish.is_favorite || false
  editDishEntryIds.value = dish.entries.map(e => e.id)
  editDishEatenAt.value = dish.entries[0]?.eaten_at || null
  // Правило дублирования блюда — только если все его записи в одном наборе
  const dupIds = new Set(dish.entries.map(e => e.duplicate_group_id).filter(Boolean))
  editDishDuplicateRule.value = (dupIds.size === 1 && dish.entries.every(e => e.duplicate_group_id))
    ? dish.entries[0].duplicate_rule
    : null
  editDishIsEditedCooking.value = false
  editDishCookedWeightG.value = null

  // Сохраняем note/hunger/satiety приёма пищи, если первый entry группы
  // принадлежит редактируемому блюду (при удалении+создании они бы пропали)
  editDishMealMeta.value = null
  const dishEntryIds = new Set(dish.entries.map(e => e.id))
  for (const group of mealGroups.value) {
    const first = group.entries[0]
    if (!dishEntryIds.has(first.id)) continue
    const note = first.note?.trim() || null
    const hunger = first.hunger_before || 0
    const satiety = first.satiety_after || 0
    if (note || hunger || satiety) {
      editDishMealMeta.value = { note, hunger_before: hunger, satiety_after: satiety }
    }
    break
  }

  // Для is_edited_cooking блюд: загрузить рецепт и показать ингредиенты
  let hasRecipeData = false
  if (dish.dish_id) {
    try {
      const { data } = await api.get(`/cooking/dish/${dish.dish_id}`)
      // Состав, собранный сервером из записей блюда, — это одна порция блюда
      // вообще, а правим мы конкретную карточку: её вес лежит в её записях.
      hasRecipeData = data.recipe_ingredients?.length > 0 && !data.recipe_from_entries
      if (hasRecipeData) {
        // Для портионных рецептов (is_edited_cooking) — помечаем, чтобы не перезаписывать recipe_totals при редактировании
        editDishIsEditedCooking.value = !!(data.dish?.is_edited_cooking || data.dish?.cooked_weight_g > 0)

        // Ингредиенты рецепта уже содержат корректные абсолютные веса из dish_ingredients JSON.
        // Не масштабируем: cooked_weight_g может отличаться от суммы ингредиентов (ужарка/выпаривание).
        const storedCookedWeight = Number(data.dish.cooked_weight_g)

        editDishCookedWeightG.value = storedCookedWeight > 0 ? storedCookedWeight : null

        editDishItems.value = data.recipe_ingredients.map(ing => {
          const w = Math.round((ing.weight_g || 0) * 10) / 10
          const item = {
            name: ing.product_name,
            type: ing.ingredients?.length > 0 ? 'recipe' : 'direct',
            weight_g: w,
            product_id: ing.product_id || null,
            db_name: ing.db_product_name || null,
            matched: !!ing.product_id,
            // Откуда цифры у позиции без карточки — см. estimate в PhotoRecognizeDialog
            _usedFallback: !ing.product_id && ing.estimate === 'ai',
            _hasLabelData: !ing.product_id && ing.estimate === 'label',
            source: ing.product_source || ing.source || null,
            kcal:    Math.round((ing.kcal || 0) * 10) / 10,
            protein: Math.round((ing.protein || 0) * 10) / 10,
            fat:     Math.round((ing.fat || 0) * 10) / 10,
            carbs:   Math.round((ing.carbs || 0) * 10) / 10,
            fiber:   ing.fiber != null ? Math.round(ing.fiber * 10) / 10 : null,
            water:   ing.water != null ? Math.round(ing.water * 10) / 10 : null,
            per100:  ing.per100 || {},
            vitamins: ing.vitamins || null,
            // Раскладка крупы/пасты — чтобы при редактировании было видно,
            // что вес готовый, а КБЖУ взяты от сухой записи плюс вода.
            _grain: ing.grain || null,
            _grainParts: ing.grain_components || null,
            _cooking: ing.cooking || null,
          }
          // Preserve sub-ingredients (2-level nesting)
          if (ing.ingredients?.length > 0) {
            item.ingredients = ing.ingredients.map(subIng => ({
              name: subIng.name,
              weight_g: Math.round((subIng.weight_g || 0) * 10) / 10,
              per100: subIng.per100 || {},
              product_id: subIng.product_id || null,
              db_name: subIng.db_name || null,
              matched: !!subIng.product_id,
              source: subIng.source || null,
              _grain: subIng.grain || null,
              _cooking: subIng.cooking || null,
            }))
            if (ing.cooking_method) item.cooking_method = ing.cooking_method
          }
          return item
        })
        // Штуки и порции живут на записях — переносим их в позиции рецепта
        mergeEntryUnits(editDishItems.value, dish.entries)
        showEditDish.value = true
        return
      }
    } catch { /* fallback to entries */ }
  }

  // Fallback: обычный маппинг entries
  editDishItems.value = mapEntriesToEditItems(dish.entries)
  // Передаём cooked_weight_g только если у блюда есть рецептные данные —
  // для обычных блюд (фото) не передаём, чтобы portionWeight = сумма весов
  if (!editDishCookedWeightG.value && hasRecipeData) {
    const cw = dish.entries[0]?.dish_cooked_weight_g
    if (cw && Number(cw) > 0) editDishCookedWeightG.value = Number(cw)
  }
  showEditDish.value = true
}

function onDishEdited(newEntries, updatedDishName) {
  guide.editSaved()
  const oldIds = new Set(editDishEntryIds.value)
  const meta = editDishMealMeta.value

  entries.value = entries.value.filter(e => !oldIds.has(e.id))
  periodDayEntries.value = periodDayEntries.value.filter(e => !oldIds.has(e.id))
  // Добавляем новые (с dish_name для корректного отображения в MealGroup)
  const name = updatedDishName || editDishName.value
  for (const entry of newEntries) {
    entry.dish_name = name || entry.product_name
    acceptEntryForOpenDay(entry)
  }

  // Восстанавливаем note/hunger/satiety на первый entry результирующей группы.
  // Backend DELETE с ?skip_transfer=1 не переносит metadata — мы сами ставим
  // его на правильный entry и сохраняем через PATCH.
  if (meta && newEntries.length) {
    // Блюдо ушло на другой день — в открытом дне его группы нет, заметку
    // вешаем прямо на первую новую запись.
    const groups = mealGroups.value.some(g => g.entries.some(e => e.id === newEntries[0].id))
      ? mealGroups.value
      : [{ entries: [newEntries[0]] }]
    for (const group of groups) {
      if (!group.entries.some(e => e.id === newEntries[0].id)) continue
      const firstId = group.entries[0].id
      // Устанавливаем metadata через splice для гарантированной реактивности:
      // прямая мутация свойств через reactive proxy может не подхватываться
      // компонентом MealGroup (refs инициализируются однократно при setup).
      const idx = entries.value.findIndex(e => e.id === firstId)
      if (idx !== -1) {
        const updated = { ...entries.value[idx] }
        if (meta.note)          updated.note           = meta.note
        if (meta.hunger_before) updated.hunger_before  = meta.hunger_before
        if (meta.satiety_after) updated.satiety_after  = meta.satiety_after
        entries.value.splice(idx, 1, updated)
      }
      // Сохраняем в БД
      const payload = {}
      if (meta.note)          payload.note          = meta.note
      if (meta.hunger_before) payload.hunger_before = meta.hunger_before
      if (meta.satiety_after) payload.satiety_after = meta.satiety_after
      if (Object.keys(payload).length) api.patch(`/diary/${firstId}`, payload).catch(() => {})
      break
    }
  }

  showEditDish.value = false
  updateDiaryCache()
}

function onDishDeleted(deletedIds) {
  // Backend уже перенёс note/hunger/satiety в БД при DELETE.
  // Дублируем перенос локально, чтобы UI обновился сразу без рефетча.
  const idsSet = new Set(deletedIds)
  for (const group of mealGroups.value) {
    const first = group.entries[0]
    if (!idsSet.has(first.id)) continue
    const note    = first.note?.trim() ? first.note : null
    const hunger  = first.hunger_before || 0
    const satiety = first.satiety_after || 0
    if (!note && !hunger && !satiety) continue
    const survivor = group.entries.find(e => !idsSet.has(e.id))
    if (!survivor) continue
    if (note && !survivor.note)                survivor.note           = note
    if (hunger && !survivor.hunger_before)     survivor.hunger_before  = hunger
    if (satiety && !survivor.satiety_after)    survivor.satiety_after  = satiety
  }
  entries.value = entries.value.filter(e => !idsSet.has(e.id))
  periodDayEntries.value = periodDayEntries.value.filter(e => !idsSet.has(e.id))
  showEditDish.value = false
  updateDiaryCache()
}

function onFavoriteToggled({ dishId, isFavorite }) {
  entries.value = entries.value.map(e =>
    e.dish_id === dishId ? { ...e, is_favorite: isFavorite } : e
  )
  periodDayEntries.value = periodDayEntries.value.map(e =>
    e.dish_id === dishId ? { ...e, is_favorite: isFavorite } : e
  )
  updateDiaryCache()
}
</script>

<style scoped>
/* Приглашение заполнить анкету вместо запрета ходить по приложению.
   Тон — подсказки, а не ошибки: человек ничего не сломал, просто ещё не
   рассказал о себе. Цвет тот же, что у поля корректировки ИИ. */
.norm-hint {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #FEEDB0;
  border-radius: 16px;
  padding: 10px 12px;
}
.norm-hint__text {
  flex: 1;
  min-width: 0;
  font-family: Inter, sans-serif;
  font-size: var(--fs-13);
  line-height: 1.35;
  color: var(--ink);
  margin: 0;
}
.norm-hint__btn {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: var(--fs-13);
  font-weight: 600;
  color: var(--cream);
  background: #41291A;
  border-radius: 12px;
  padding: 7px 12px;
  transition: transform .15s;
}
.norm-hint__btn:active { transform: scale(.97); }

.scrollbar-hide { scrollbar-width: none; }
.scrollbar-hide::-webkit-scrollbar { display: none; }

/* Перевёрнутые скругления у активного таба */
.tab-active::before,
.tab-active::after {
  content: '';
  position: absolute;
  bottom: 0;
  width: 14px;
  height: 14px;
  background-color: rgba(250, 243, 232, 0.75);
}
.tab-active::before {
  left: -14px;
  -webkit-mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top left, transparent 14px, black 14.5px);
}
.tab-active::after {
  right: -14px;
  -webkit-mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
  mask: radial-gradient(circle at top right, transparent 14px, black 14.5px);
}
.tab-active.tab-first::before { display: none; }
.tab-active.tab-last::after { display: none; }

/* Без теней */
button, .tab-active {
  box-shadow: none;
}

/* Blur fade зоны */
.blur-fade-top {
  position: fixed;
  top: calc(72px + var(--safe-top));
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  height: 20px;
  z-index: 40;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(250,243,232,0.7), transparent);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,0.8), transparent);
  mask-image: linear-gradient(to bottom, rgba(0,0,0,0.8), transparent);
}
.blur-fade-bottom {
  position: fixed;
  bottom: 72px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  height: 20px;
  z-index: 40;
  pointer-events: none;
  background: linear-gradient(to top, rgba(250,243,232,0.7), transparent);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  -webkit-mask-image: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  mask-image: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
}

/* Разделитель между приёмами — линия 1,4 px сплошным ink, как кружки голода
 * и сытости, «+» в круге и иконка «Заметка» (решение владельца 10.10.2026).
 * Векторной линией (SVG-фон), а не height: 1.4px или тенью: и высоту, и тень
 * Safari округляет до пикселей экрана (1 px при плотности 2, 1,67 при 3),
 * линии SVG — нет. Растягивается по ширине, толщина от этого не меняется. */
.meal-divider {
  height: 2px;
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 2' preserveAspectRatio='none'%3E%3Cline x1='0' y1='1' x2='100' y2='1' stroke='%2341291A' stroke-width='1.4' vector-effect='non-scaling-stroke'/%3E%3C/svg%3E") center / 100% 2px no-repeat;
}

/* FAB «+» — ровно там, где кнопка камеры в AddFoodDialog: шторка шириной
 * до 430px у низа экрана, кнопка 90px в 20px от её правого края, низ — на
 * 17px выше блока поля ввода (8 + 53 + 20 = 81px). Итого 98px от низа экрана.
 * Меняется геометрия там — менять и здесь. Над навбаром (72px + инсет) всё
 * равно должна оставаться: на Android с большим системным инсетом навбар выше. */
.fab-add {
  width: 90px;
  height: 90px;
  /* --sheet-bottom: в нативном Android шторка добавления поднята над
     системными кнопками, и камера вместе с ней — «+» поднимается так же */
  bottom: max(calc(98px + var(--sheet-bottom, 0px)), calc(72px + 12px + var(--nav-inset)));
  right: 20px;
}
@media (min-width: 430px) {
  .fab-add {
    right: calc(50% - 215px + 20px);
  }
}

/* Zoya feedback formatting (v-html → need :deep) */
.zoya-feedback {
  font-family: Inter, sans-serif;
  font-size: var(--fs-14);
  line-height: 1.55;
  color: var(--ink);
}
.zoya-feedback :deep(p) {
  margin: 0 0 10px 0;
}
.zoya-feedback :deep(p:last-child) {
  margin-bottom: 0;
}
.zoya-feedback :deep(.zf-spacer) {
  height: 6px;
}
.zoya-feedback :deep(strong) {
  font-weight: 600;
}
.zoya-feedback :deep(.zf-list) {
  margin: 4px 0 10px 0;
  padding-left: 22px;
  list-style: disc;
}
.zoya-feedback :deep(.zf-list li) {
  margin: 4px 0;
}
.zoya-feedback :deep(ol.zf-list) {
  list-style: decimal;
}
.zoya-feedback :deep(.zf-h1) {
  font-size: var(--fs-16);
  font-weight: 700;
  margin: 0 0 6px 0;
}
.zoya-feedback :deep(.zf-h2) {
  font-size: var(--fs-18);
  font-weight: 600;
  margin: 0 0 4px 0;
}
.zoya-feedback :deep(.zf-h3) {
  font-size: var(--fs-14);
  font-weight: 600;
  margin: 0 0 4px 0;
}

/* Пустой день — надпись + стрелка к кнопке "+" */
.empty-day-container {
  /* Закреплена в окне между таб-баром (72px) и NavBar (80px + инсет), а не в
     потоке: карточка «Первые шаги» и блок норм сверху не сдвигают текст со
     стрелкой, и при раскрытии карточки он остаётся на месте (замечание
     владельца 26.10.2026). Нажатий не ловит, карточка рисуется поверх. */
  position: fixed;
  top: calc(72px + var(--safe-top));
  bottom: calc(80px + var(--nav-inset));
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 430px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
}
.empty-day-text {
  font-family: Inter, sans-serif;
  font-size: var(--fs-22);
  font-weight: 300;
  color: var(--ink);
  text-align: center;
  line-height: 1.3;
  opacity: 0.7;
}
.empty-day-arrow-wrap {
  margin-top: 8px;
  margin-left: 15%;
}

/* Toast порции */

/* Вкладка «период»: чипы диапазона и переключатель БЖУ в карточке */
/* Селектор периода: единственный акцент на экране; сжимается с многоточием,
   чтобы не наехать на «Аналитика» на узких телефонах */
.day-completeness { margin-bottom:8px; }
/* Compact row; the switch keeps its 44px touch target in the surrounding space. */
.week-completeness-slot { height:28px; display:flex; align-items:center; }
.week-completeness-slot > .day-status { position:relative; top:-6px; }
.day-heading { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:8px; }
.day-heading h2 { font-family:Inter,sans-serif; font-size:var(--fs-26); line-height:1.1; font-weight:600; color:var(--ink); min-width:0; }
.psel { display: inline-flex; align-items: center; gap: 6px; height: 36px; min-width: 0; max-width: 60%; padding: 0 10px 0 12px; border-radius: 999px; background: var(--ink); color: var(--cream); font-family: Inter, sans-serif; font-size: var(--fs-14); font-weight: 600; white-space: nowrap; }
.psel-cancel { outline: 0; background: transparent; color: var(--ink); border: 1px solid var(--line); padding: 0 16px; }
.psel-cancel:focus-visible { outline: 1px solid var(--ink-60); outline-offset: 2px; }
.pmenu { position: absolute; right: 12px; top: 48px; z-index: 30; width: 250px; background: #fff; border: 1px solid var(--line); border-radius: 16px; box-shadow: 0 12px 32px -12px rgba(27, 17, 11, .35); padding: 6px; display: flex; flex-direction: column; }
.pitem { display: flex; align-items: center; justify-content: space-between; gap: 10px; width: 100%; padding: 11px 12px; border-radius: 11px; text-align: left; font-family: Inter, sans-serif; font-size: var(--fs-16); font-weight: 500; color: var(--ink); }
.pitem.on { background: var(--ink-10); font-weight: 600; }
.pitem:last-child { border-top: 1px solid var(--line); border-radius: 0 0 11px 11px; margin-top: 4px; }
.pcap { font-size: var(--fs-13); font-weight: 400; color: var(--ink-70); }
</style>
